import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import Select2Dropdown from '../common/Select2Dropdown';
import { openFilePreview } from '../common/FilePreviewModal';
import ConfirmDialog from '../common/ConfirmDialog';
import PopoutWindow from '../common/PopoutWindow';
import PayableExpensesView from './PayableExpensesView';
import { TRANSPORTER_PAYABLE_CHARGES } from '../../transporterPayableChargesConfig';

const API_PAYABLES = '/api/admin/other-payables/';
const API_JOB_SEARCH = '/api/documentation/jobs/search_by_idf/';

const INVOICE_TYPES = [
    { value: 'PROFORMA', label: 'Proforma' },
    { value: 'TAX', label: 'Tax Invoice' },
];

const CURRENCIES = [
    { value: 'USD', label: 'USD' },
    { value: 'TZS', label: 'TZS' },
];

const JOB_SEARCH_FIELDS = [
    { id: 'job_no', text: 'JOB NO.' },
    { id: 'bl_no', text: 'BL NO.' },
    { id: 'idf_no', text: 'IDF NO.' },
    { id: 'kk_file_no', text: 'KK FILE NO.' },
];

const DEFAULT_DOCUMENT_TYPES = [
    { id: 'PROFORMA INVOICE',   text: 'PROFORMA INVOICE' },
    { id: 'TAX INVOICE',        text: 'TAX INVOICE' },
    { id: 'RECEIPT',            text: 'RECEIPT' },
    { id: 'COMMERCIAL INVOICE', text: 'COMMERCIAL INVOICE' },
    { id: 'BL',     text: 'BL' },
    { id: 'BILL OF LADING / AWB / RCN',     text: 'BILL OF LADING / AWB / RCN' },
    { id: 'PROOF OF PAYMENT',   text: 'PROOF OF PAYMENT' },
];

const getAuthHeaders = (isForm = false) => {
    const headers = {
        'Authorization': `Bearer ${localStorage.getItem('kk_token') || localStorage.getItem('tp_token')}`
    };
    if (!isForm) {
        headers['Content-Type'] = 'application/json';
    }
    return headers;
};

// Helper for currency formatting
const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount || 0);
};

const formatFxRate = (amount) => {
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 }).format(amount || 0);
};

const recalculateItemAmounts = (item, invoiceCurrencyDifferent, invoiceCurrencyData, fxRoundOff, isTransporter = false) => {
    const r2 = (n) => Math.round(n * 100) / 100;
    const isAdjustment = item.charge_name === 'ADJUSTMENT';

    if (isTransporter && !isAdjustment) {
        let total = parseFloat(item.total_amount) || 0;
        const savedAmount = parseFloat(item.amount) || 0;
        const savedVat = parseFloat(item.vat_amount) || 0;
        if (!total && (savedAmount || savedVat)) {
            // Back-fill total from saved net + VAT when loading existing records
            total = r2(savedAmount + savedVat);
        }

        if (item.add_vat) {
            const amount = r2(total / 1.18);
            const vatAmount = r2(total - amount);
            return {
                ...item,
                amount: amount.toFixed(2),
                vat_amount: vatAmount.toFixed(2),
                total_amount: total.toFixed(2),
                units: 1,
                unit_type: 'Unit',
                unit_rate: amount.toFixed(2),
            };
        }

        return {
            ...item,
            amount: total.toFixed(2),
            vat_amount: '0.00',
            total_amount: total.toFixed(2),
            units: 1,
            unit_type: 'Unit',
            unit_rate: total.toFixed(2),
        };
    }

    const units = parseFloat(item.units) || 0;
    const rate = parseFloat(item.unit_rate) || 0;

    let amount = parseFloat(item.amount) || 0;
    let foreignAmount = parseFloat(item.foreign_amount) || 0;

    const fxRate = parseFloat(invoiceCurrencyData?.fx_rate || 1);
    const effectiveRate = (invoiceCurrencyDifferent && fxRate) ? (fxRoundOff ? Math.round(fxRate * 100) / 100 : fxRate) : 1;

    if (isAdjustment) {
        // The saved total_amount is the authoritative value for adjustments
        amount = parseFloat(item.total_amount) || amount;
    }

    if (!isAdjustment) {
        if (invoiceCurrencyDifferent) {
            if (foreignAmount > 0) {
                // foreign_amount is the saved USD total; amount is TZS
                amount = r2(foreignAmount * effectiveRate);
            } else {
                // Derive USD from units * rate, then convert to TZS
                foreignAmount = r2(units * rate);
                amount = r2(foreignAmount * effectiveRate);
            }
        } else {
            amount = r2(units * rate);
            foreignAmount = 0;
        }
    }

    const vatAmount = item.add_vat ? r2(amount * 0.18) : 0;
    const totalAmount = item.add_vat ? r2(amount + vatAmount) : amount;

    return {
        ...item,
        amount: amount.toFixed(2),
        foreign_amount: invoiceCurrencyDifferent && !isAdjustment ? foreignAmount.toFixed(2) : item.foreign_amount,
        vat_amount: vatAmount.toFixed(2),
        total_amount: totalAmount.toFixed(2),
    };
};

// Foreign-currency amount/VAT/total for a line item, shown when invoice currency differs.
const itemForeignAmount = (item) => {
    const fa = parseFloat(item.foreign_amount) || 0;
    if (fa > 0) return fa;
    const viaUnits = (parseFloat(item.units) || 0) * (parseFloat(item.unit_rate) || 0);
    return viaUnits > 0 ? viaUnits : (parseFloat(item.amount) || 0);
};
const itemForeignVat = (item) =>
    item.charge_name === 'ADJUSTMENT' ? 0 : (item.add_vat ? Math.round(itemForeignAmount(item) * 0.18 * 100) / 100 : 0);
const itemForeignTotal = (item) =>
    item.charge_name === 'ADJUSTMENT' ? 0 : Math.round((itemForeignAmount(item) + itemForeignVat(item)) * 100) / 100;

const formatDateDisplay = (value) => {
    if (!value) return '-';
    const str = String(value);
    const match = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) return `${match[3]}/${match[2]}/${match[1]}`;
    const dt = new Date(value);
    if (isNaN(dt.getTime())) return str;
    return dt.toLocaleDateString('en-GB');
};

const formatDateTimeDisplay = (value) => {
    if (!value) return '-';
    const dt = new Date(value);
    if (isNaN(dt.getTime())) return String(value);
    return `${dt.toLocaleDateString('en-GB')} ${dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};

// Helper for live input formatting (commas only)
const addCommasToInput = (val) => {
    if (val === '' || val === null || val === undefined) return '';
    const clean = val.toString().replace(/,/g, '');
    const parts = clean.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return parts.join('.');
};

const getTransferCharge = (amount, brackets) => {
    const numericAmount = parseFloat(amount);
    if (!numericAmount || Number.isNaN(numericAmount) || numericAmount <= 0) return 0;

    const normalizedBrackets = Array.isArray(brackets) ? brackets : [];
    const matchedBracket = normalizedBrackets.find(bracket => {
        const minAmount = parseFloat(bracket.min_amount ?? bracket.min ?? bracket.minimum_amount ?? 0);
        const maxValue = bracket.max_amount ?? bracket.max ?? bracket.maximum_amount;
        const maxAmount = maxValue === null || maxValue === '' || maxValue === undefined ? Infinity : parseFloat(maxValue);
        return numericAmount >= minAmount && numericAmount <= maxAmount;
    });

    return matchedBracket ? parseFloat(matchedBracket.rate ?? matchedBracket.amount ?? matchedBracket.charge ?? 0) : 0;
};

// Component for formatted number input
const FormattedNumberInput = ({ value, onChange, disabled, style, required, placeholder, allowNegative = false, hidden=false }) => {
    const [displayValue, setDisplayValue] = useState('');
    const [isFocused, setIsFocused] = useState(false);

    useEffect(() => {
        if (!isFocused) {
            if (value !== '' && value !== null && !isNaN(value)) {
                setDisplayValue(formatCurrency(value));
            } else {
                setDisplayValue('');
            }
        }
    }, [value, isFocused]);

    const handleChange = (e) => {
        const val = e.target.value;
        // Validation: optional minus sign, digits, commas, one dot
        const regex = allowNegative ? /^-?[\d,]*\.?[\d]*$/ : /^[\d,]*\.?[\d]*$/;
        if (regex.test(val)) {
             const raw = val.replace(/,/g, '');
             onChange(raw);
             setDisplayValue(addCommasToInput(val));
        }
    };

    const handleBlur = () => {
        setIsFocused(false);
        if (value !== '' && value !== null && !isNaN(value)) {
            setDisplayValue(formatCurrency(value));
        }
    };

    const handleFocus = () => {
        setIsFocused(true);
        if (value !== '' && value !== null && !isNaN(value)) {
            setDisplayValue(addCommasToInput(value));
        }
    };

    return (
        <input
            type="text"
            value={displayValue}
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            disabled={disabled}
            style={{ ...style, textAlign: 'right' }}
            required={required}
            className="form-control"
            placeholder={placeholder}
            hidden={hidden}
        />
    );
};

const OtherPayablesForm = ({ 
    mode, 
    initialData, 
    onClose, 
    onSave,
    approvalMode,
    canApprove,
    onApprove,
    onReject,
    onClientPaid,
    rejectReason,
    setRejectReason,
    showRejectInput,
    setShowRejectInput,
    actionLoading,
    tasacLink,
    receiptUploadOnly,
    hideClientPaid,
    showViewExpenses,
    module,
    popoutMode,
    toastContainerId,
    isTransporterPortal
}) => {
    const toastOptions = toastContainerId ? { containerId: toastContainerId } : undefined;
    const notifyError = (message) => toast.error(message, toastOptions);
    const notifySuccess = (message) => toast.success(message, toastOptions);

    const isView = mode === 'view';
    const isTransporter = !!(initialData?.transporter);
    const isStage1Approval = initialData && initialData.status === 'PENDING' && approvalMode;
    const displayAsView = isView || receiptUploadOnly;
    const canManageDocumentRows = mode === 'add'
        || mode === 'create'
        || receiptUploadOnly
        || (mode === 'edit' && initialData?.status === 'REJECTED')
        || (isStage1Approval && isTransporter);
    // Standard fields are disabled in View OR Receipt Upload Only mode
    const isFieldDisabled = isView || receiptUploadOnly; 
    // Invoice/Document fields are only disabled in View mode (editable in Receipt Upload)
    // EXCEPTION: Only Invoice Type and Invoice No should be editable in receipt mode. Date etc disabled.
    const isInvoiceEditDisabled = isView;
    const isDateDisabled = isView || receiptUploadOnly;

    // Filter Options for Create Mode
    const invoiceOptions = isTransporter
        ? [{ value: 'TAX', label: 'Tax Invoice' }]
        : ((mode === 'add' || mode === 'create') && initialData?.invoice_type !== 'TAX'
            ? INVOICE_TYPES.filter(t => t.value !== 'TAX')
            : INVOICE_TYPES);

    const [documentTypeBaseOptions, setDocumentTypeBaseOptions] = useState(DEFAULT_DOCUMENT_TYPES);
    const [showExpensesModal, setShowExpensesModal] = useState(false);

    let documentTypeOptions = documentTypeBaseOptions;
    if (isTransporter) {
        documentTypeOptions = documentTypeBaseOptions.filter(t => {
            const id = (t.id || '').toLowerCase();
            return id === 'tax invoice' || (isStage1Approval && id === 'proof of payment');
        });
    } else if (mode === 'add' || mode === 'create') {
        if (initialData?.invoice_type !== 'TAX') {
            documentTypeOptions = documentTypeBaseOptions.filter(t => (t.id || '').toLowerCase() !== 'tax invoice'.toLowerCase());
        }
    } else if (receiptUploadOnly) {
        // Pending Receipts Mode: allow tax invoice upload and optional/required receipt (case-insensitive).
        documentTypeOptions = documentTypeBaseOptions.filter(t => {
            const id = (t.id || '').toLowerCase();
            return id === 'tax invoice'.toLowerCase() || id === 'receipt'.toLowerCase();
        });
    }

    const [jobDetails, setJobDetails] = useState(null);
    const [jobSearchField, setJobSearchField] = useState('');
    const [jobSearchTerm, setJobSearchTerm] = useState('');
    const [idfOptions, setIdfOptions] = useState([]);
    const [activeDropdownIndex, setActiveDropdownIndex] = useState(-1);
    const [loadingInfo, setLoadingInfo] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // Dynamic Options
    const [chargeOptions, setChargeOptions] = useState([]); // [{id: 'Name', text: 'Name', add_vat: bool}]
    const [unitOptions, setUnitOptions] = useState([]); 
    const [vendorOptions, setVendorOptions] = useState([]);

    // Form Fields
    const [formData, setFormData] = useState({
        job: '',
        invoice_type: 'PROFORMA',
        invoice_no: '',
        proforma_invoice_no: '',
        tax_invoice_no: '',
        invoice_date: '',
        usd_rate: '1.00',
        currency: 'TZS',
        vendor: '',
        remarks: '',
        pay_by_control_no: false,
        control_no: '',
        control_no_type: '', // GEPG or OTHER
        total_amount_gepg: '' // Helper (unused now if itemizing)
    });

    const [items, setItems] = useState(() =>
        !initialData ? [{ charge_name: '', units: '', unit_type: '', unit_rate: '', amount: 0, add_vat: false }] : []
    );
    const [adjustmentErrors, setAdjustmentErrors] = useState({});
    const [documents, setDocuments] = useState([]);
    const [docIdsToDelete, setDocIdsToDelete] = useState([]); 
    const [savingDocIndex, setSavingDocIndex] = useState(null);

    const proformaNoDisplay = formData.proforma_invoice_no || (initialData?.invoice_type === 'PROFORMA' ? initialData?.invoice_no : '') || '-';

    const requiresReceiptDoc = !initialData?.vendor_tax_invoice_has_receipt;

    const [invoiceCurrencyDifferent, setInvoiceCurrencyDifferent] = useState(false);
    const [invoiceCurrencyData, setInvoiceCurrencyData] = useState({
        currency: 'USD',
        total_fx: '',
        total_tzs: '',
        fx_rate: ''
    });
    const [fxRoundOff, setFxRoundOff] = useState(true);

    const [efdVerified, setEfdVerified] = useState(false); // EFD Verification Checkbox State
    const [showEFDWarning, setShowEFDWarning] = useState(false);
    const [showSaveProceedDialog, setShowSaveProceedDialog] = useState(false);
    const [showApprovalHistory, setShowApprovalHistory] = useState(true);
    const [showClientPaidConfirm, setShowClientPaidConfirm] = useState(false);
    const [clientPaidDate, setClientPaidDate] = useState(new Date().toISOString().split('T')[0]);

    useEffect(() => {
        if (invoiceCurrencyDifferent && invoiceCurrencyData.total_fx && invoiceCurrencyData.total_tzs) {
            const fx = parseFloat(invoiceCurrencyData.total_fx);
            const tzs = parseFloat(invoiceCurrencyData.total_tzs);
            if (!isNaN(fx) && !isNaN(tzs) && fx !== 0) {
                const rate = (tzs / fx).toFixed(4);
                setInvoiceCurrencyData(prev => ({ ...prev, fx_rate: rate }));
            }
        }
    }, [invoiceCurrencyData.total_fx, invoiceCurrencyData.total_tzs, invoiceCurrencyDifferent]);

    // In DIFF mode, conversion currency is fixed to the opposite of base currency.
    useEffect(() => {
        if (!invoiceCurrencyDifferent) return;

        const targetCurrency = formData.currency === 'USD' ? 'TZS' : 'USD';
        if (invoiceCurrencyData.currency !== targetCurrency) {
            setInvoiceCurrencyData(prev => ({ ...prev, currency: targetCurrency }));
        }
    }, [formData.currency, invoiceCurrencyDifferent, invoiceCurrencyData.currency]);

    // Totals
    const [netTotal, setNetTotal] = useState(0);
    const [vatTotal, setVatTotal] = useState(0);
    const [grossTotal, setGrossTotal] = useState(0);
    const [totalForeignAmount, setTotalForeignAmount] = useState(0);

    // Confirmation Dialog
    const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });

    const showConfirm = (title, message, onConfirm) => {
        setConfirmDialog({ open: true, title, message, onConfirm });
    };

    const closeConfirm = () => {
        setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
    };

    // Stage 2 Check: Approving FROM Stage 2 (which is APPROVED 1) -> To Stage 3
    const isStage2Approval = initialData && initialData.status === 'APPROVED 1' && approvalMode;
    // Stage 3 Check: Approving FROM Stage 3 (which is APPROVED 2) -> To Expenses (APPROVED 3)
    const isStage3Approval = initialData && initialData.status === 'APPROVED 2' && approvalMode;
    // Expenses View: Final Approved State
    const isExpensesView = initialData && (initialData.status === 'APPROVED 3' || initialData.status === 'CLIENT_PAID' || initialData.status === 'CLIENT PAID' || mode === 'expenses');
    const isUploadedReceiptReview = !!(
        approvalMode &&
        canApprove &&
        hideClientPaid &&
        isView &&
        !receiptUploadOnly &&
        ['APPROVED 3', 'CLIENT PAID', 'CLIENT_PAID'].includes(initialData?.status) &&
        initialData?.receipt_uploaded &&
        !initialData?.receipt_approved
    );
    const hasAllowReplaceSelected = documents.some((doc) => {
        const type = (doc.type || '').toLowerCase();
        return (type === 'tax invoice' || type === 'receipt') && !!doc.allowReplace;
    });
    
    // Stage 2 Fields
    const [stage2Data, setStage2Data] = useState({
        bank: '',
        cheque_no: '',
        collected_by: '',
        paid_by: '' // Not in model yet, assuming user select or auto
    });
    // Stage 3 Fields
    const [stage3Data, setStage3Data] = useState({
        payment_date: new Date().toISOString().split('T')[0],
        tiss_tips: ''
    });
    const [showStage3TissError, setShowStage3TissError] = useState(false);

    const [chequePayables, setChequePayables] = useState([]);
    const [bankOptions, setBankOptions] = useState([]);
    const [rejectionTarget, setRejectionTarget] = useState(null);

    const isTissOrTipsSelected = stage3Data.tiss_tips === 'TISS' || stage3Data.tiss_tips === 'TIPS';
    const isGepgPayable = !!(formData.control_no && formData.control_no_type === 'GEPG');
    const isCash = (stage3Data.payment_source || initialData?.bank_account || '') === 'CASH';

    // Filter bank options based on selected currency
    const filteredBankOptions = useMemo(() => {
        const currency = formData.currency || initialData?.currency || 'TZS';
        return bankOptions.filter(bank => bank.currency === currency);
    }, [bankOptions, formData.currency, initialData?.currency]);

    const selectedStage3BankAccount = useMemo(() => {
        const selectedBankId = stage3Data.payment_source || initialData?.bank_account || '';
        if (!selectedBankId || selectedBankId === 'CASH') return null;
        return bankOptions.find(bank => String(bank.id) === String(selectedBankId)) || null;
    }, [bankOptions, stage3Data.payment_source, initialData?.bank_account]);

    const selectedStage3Brackets = useMemo(() => {
        if (!selectedStage3BankAccount || !isTissOrTipsSelected) return [];
        return stage3Data.tiss_tips === 'TIPS'
            ? (selectedStage3BankAccount.tips_brackets || [])
            : (selectedStage3BankAccount.tiss_brackets || []);
    }, [selectedStage3BankAccount, stage3Data.tiss_tips, isTissOrTipsSelected]);

    const stage3BasePayableAmount = useMemo(() => {
        const base = parseFloat(grossTotal) || 0;
        const usdRate = parseFloat(formData.usd_rate) || 1;
        return formData.currency === 'TZS' ? base : base * usdRate;
    }, [grossTotal, formData.currency, formData.usd_rate]);

    const stage3TissTipsAmount = useMemo(() => {
        return getTransferCharge(stage3BasePayableAmount, selectedStage3Brackets);
    }, [stage3BasePayableAmount, selectedStage3Brackets]);

    const stage3NewPayableAmount = useMemo(() => {
        return stage3BasePayableAmount + stage3TissTipsAmount;
    }, [stage3BasePayableAmount, stage3TissTipsAmount]);

    const gepgBrackets = useMemo(() => {
        if (!isGepgPayable || !selectedStage3BankAccount) return [];
        return selectedStage3BankAccount.gepg_brackets || [];
    }, [isGepgPayable, selectedStage3BankAccount]);

    const gepgAmount = useMemo(() => {
        return getTransferCharge(stage3BasePayableAmount, gepgBrackets);
    }, [stage3BasePayableAmount, gepgBrackets]);

    const stage3NewPayableWithGepg = useMemo(() => {
        return stage3BasePayableAmount + gepgAmount;
    }, [stage3BasePayableAmount, gepgAmount]);

    const stage3NewPayableTotal = useMemo(() => {
        return stage3BasePayableAmount + stage3TissTipsAmount + gepgAmount;
    }, [stage3BasePayableAmount, stage3TissTipsAmount, gepgAmount]);

    const getJobSearchValue = (job, field) => {
        if (!job) return '';
        if (field === 'job_no') return job.id || '';
        if (field === 'bl_no') return job.bl_no || '';
        if (field === 'kk_file_no') return job.kk_file_no || '';
        return job.idf_no || '';
    };

    const getDefaultReadOnlySearchInfo = () => {
        if (!jobDetails && !initialData) {
            return { field: 'idf_no', value: '' };
        }

        const data = jobDetails || {};
        const idf = data.idf_no || initialData?.idf_number || '';
        const jobNo = data.id || initialData?.job_number || '';
        const bl = data.bl_no || initialData?.bl_number || '';
        const kkFile = data.kk_file_no || initialData?.file_no || '';

        if (idf) return { field: 'idf_no', value: idf };
        if (jobNo) return { field: 'job_no', value: jobNo };
        if (bl) return { field: 'bl_no', value: bl };
        if (kkFile) return { field: 'kk_file_no', value: kkFile };
        return { field: 'idf_no', value: '' };
    };

    // Rejection Options Logic
    const getRejectionOptions = () => {
        if (isStage2Approval) return [
            { id: 'CREATOR', text: 'CREATOR' },
            { id: 'STAGE 1', text: 'STAGE 1' }
        ];
        if (isStage3Approval) return [
            { id: 'CREATOR', text: 'CREATOR' },
            { id: 'STAGE 1', text: 'STAGE 1' },
            { id: 'STAGE 2', text: 'STAGE 2' }
        ];
        return []; 
    };

    const handleRejectionSubmit = () => {
         if (!rejectReason) { notifyError("Reason required"); return; }
         showConfirm(
             'Confirm Rejection',
             'Are you sure you would like to Reject this Payable?',
             () => executeRejectionSubmit()
         );
    };

    const executeRejectionSubmit = () => {
         let target = 'REJECTED'; // Default to Creator
         
         if (isStage2Approval) {
             if (!rejectionTarget) { notifyError("Select Rejection Target"); return; }
             if (rejectionTarget === 'STAGE 1') target = 'PENDING';
             // CREATOR is default REJECTED
         }
         else if (isStage3Approval) {
             if (!rejectionTarget) { notifyError("Select Rejection Target"); return; }
             if (rejectionTarget === 'STAGE 1') target = 'PENDING';
             if (rejectionTarget === 'STAGE 2') target = 'APPROVED 1';
         }

         // For Stage 1 (isStage1 check handled by absence of others), no dropdown, automatic to Creator (REJECTED)
         // Assuming onReject handles (reason, status) pair
         if (onReject) onReject(rejectReason, target);
    };

    // Fetch Options
    const fetchChequePayables = async (chequeNo) => {
        try {
            const res = await fetch(`${API_PAYABLES}get_by_cheque/?cheque_no=${chequeNo}`, { headers: getAuthHeaders() });
            if (res.ok) {
                const data = await res.json();
                setChequePayables(data);
            }
        } catch (error) {
            console.error("Failed to fetch cheque payables", error);
        }
    };

    useEffect(() => {
        const fetchOptions = async () => {
            try {
                const fetchModule = isTransporter ? 'TRANSPORTER' : module;
                const moduleParam = fetchModule ? `?module=${encodeURIComponent(fetchModule)}` : '';
                const res = await fetch(`${API_PAYABLES}get_options/${moduleParam}`, { headers: getAuthHeaders() });
                if (res.ok) {
                    const data = await res.json();
                    if (isTransporter) {
                        // Transporter charges now come from PayableCharge master
                        // data (TRANSPORTER department) — the hardcoded list is a
                        // fallback in case none have been configured yet.
                        // cash-only " - CASH/OFFICE" variants belong to the
                        // cash-payable form and are excluded here.
                        const apiCharges = Array.isArray(data.charge_names)
                            ? data.charge_names.filter((c) => c.cash !== true)
                            : [];
                        setChargeOptions(apiCharges.length ? apiCharges : TRANSPORTER_PAYABLE_CHARGES);
                    } else if (data.charge_names) {
                        // Add ADJUSTMENT to charge options
                        const chargeNamesWithAdjustment = [
                            ...data.charge_names,
                            { id: 'ADJUSTMENT', text: 'ADJUSTMENT', add_vat: false }
                        ];
                        setChargeOptions(chargeNamesWithAdjustment);
                    }
                    if(data.vendors) setVendorOptions([...data.vendors].sort((a, b) => a.text.localeCompare(b.text)));
                    if(data.banks) setBankOptions(data.banks);
                    if (Array.isArray(data.document_types) && data.document_types.length > 0) {
                        const apiOptions = data.document_types.map((name) => ({ id: name.toUpperCase(), text: name.toUpperCase() }));
                        const merged = [
                            ...DEFAULT_DOCUMENT_TYPES,
                            ...apiOptions.filter(a => !DEFAULT_DOCUMENT_TYPES.some(d => d.id === a.id)),
                        ].sort((a, b) => a.text.localeCompare(b.text));
                        setDocumentTypeBaseOptions(merged);
                    }
                }
            } catch (error) {
                console.error("Failed to fetch options", error);
            }
        };
        fetchOptions();
    }, []);

    // Ensure a pre-selected vendor (e.g. from the transporter portal) is available in the dropdown
    useEffect(() => {
        if (!initialData?.vendor || !initialData?.vendor_name) return;
        setVendorOptions(prev => {
            if (prev.some(v => String(v.id) === String(initialData.vendor))) return prev;
            return [{ id: initialData.vendor, text: initialData.vendor_name }, ...prev];
        });
    }, [initialData?.vendor, initialData?.vendor_name]);

    // Initialize logic
    useEffect(() => {
        const init = async () => {
             if (initialData) {
                const initialProformaNo = initialData.proforma_invoice_no || (initialData.invoice_type === 'PROFORMA' ? initialData.invoice_no : '');
                const initialTaxNo = initialData.tax_invoice_no || (initialData.invoice_type === 'TAX' ? initialData.invoice_no : '');
                setFormData({
                    job: initialData.job || initialData.job_number,
                    invoice_type: isTransporter ? 'TAX' : (receiptUploadOnly ? 'TAX' : (initialData.invoice_type || 'PROFORMA')),
                    invoice_no: receiptUploadOnly ? (initialTaxNo || '') : (initialData.invoice_no || ''),
                    proforma_invoice_no: initialProformaNo,
                    tax_invoice_no: initialTaxNo,
                    invoice_date: initialData.invoice_date || '',
                    usd_rate: isTransporter ? '1.00' : (initialData.usd_rate || '1.00'),
                    currency: isTransporter ? 'TZS' : (initialData.currency || 'TZS'),
                    vendor: initialData.vendor || '',
                    transporter: initialData.transporter || '',
                    remarks: initialData.remarks || '',
                    pay_by_control_no: initialData.pay_by_control_no || false,
                    control_no: initialData.control_no || '',
                    control_no_type: initialData.control_no_type || '',
                    total_amount_gepg: initialData.control_no_type === 'GEPG' ? initialData.total_amount : ''
                });

                // Infer Different Currency Scenario (Fix for Displaying TZS Amount when saved with FX Rate logic)
                const isDiff = (initialData.currency === 'TZS' && parseFloat(initialData.usd_rate || 0) > 100);
                let loadedItems = [];

                if (initialData.items && Array.isArray(initialData.items)) {
                     loadedItems = initialData.items.map(item => ({ ...item }));
                }

                if (isDiff) {
                    const fxRoundOffValue = initialData.fx_round_off !== false;
                    const tempCurrencyData = {
                        currency: 'USD',
                        fx_rate: initialData.usd_rate,
                        total_fx: initialData.total_fx || '',
                        total_tzs: initialData.total_tzs || ''
                    };
                    setInvoiceCurrencyDifferent(true);
                    setFxRoundOff(fxRoundOffValue);
                    setInvoiceCurrencyData(prev => ({
                        ...prev,
                        ...tempCurrencyData
                    }));

                    // In edit mode, recalculate derived amounts so the saved TZS amount,
                    // USD amount, VAT and total are consistent.
                    if (!isView) {
                        loadedItems = loadedItems.map(item =>
                            recalculateItemAmounts(item, true, tempCurrencyData, fxRoundOffValue, isTransporter)
                        );
                    }
                } else {
                    setInvoiceCurrencyDifferent(false);
                    // In edit mode, ensure VAT and total are consistent with the saved amount.
                    if (!isView && loadedItems.length > 0) {
                        loadedItems = loadedItems.map(item =>
                            recalculateItemAmounts(item, false, invoiceCurrencyData, fxRoundOff, isTransporter)
                        );
                    }
                }

                if (loadedItems.length > 0) {
                     setItems(loadedItems);
                } else {
                     setItems([{ charge_name: '', units: '', unit_type: '', unit_rate: '', amount: 0, add_vat: false }]);
                }

                if (initialData.status === 'APPROVED 1') {
                    setStage2Data({
                        bank: initialData.bank_account || '',
                        cheque_no: initialData.cheque_no || '',
                        collected_by: (initialData.collected_by || '').toUpperCase(),
                    });
                }
                
                // Initialize Stage 3 / Expenses Data
                if (initialData.status === 'APPROVED 2' || initialData.status === 'APPROVED 3' || initialData.status === 'CLIENT PAID' || initialData.status === 'CLIENT_PAID') {
                    setStage2Data({
                        bank: initialData.bank_account || '',
                        cheque_no: initialData.cheque_no || '',
                        collected_by: (initialData.collected_by || '').toUpperCase(),
                        paid_by: initialData.paid_by || ''
                    });

                    setStage3Data({
                         payment_date: initialData.payment_date || new Date().toISOString().split('T')[0],
                        tiss_tips: initialData.status === 'APPROVED 2' ? '' : (initialData.tiss_tips || ''),
                         payment_source: initialData.pay_by_control_no ? (initialData.bank_account || '') : ''
                    });

                    if (initialData.cheque_no) {
                        fetchChequePayables(initialData.cheque_no);
                    }
                }
                
                if (initialData.pay_by_control_no && (initialData.status !== 'APPROVED 2' && initialData.status !== 'APPROVED 3')) {
                     // Ensure source is loaded if somehow saved earlier, or default
                     setStage3Data(prev => ({ ...prev, payment_source: initialData.bank_account || '' }));
                }
                
                // Documents
                if (initialData.documents && Array.isArray(initialData.documents)) {
                     setDocuments(initialData.documents.map(d => ({
                         type: d.document_type,
                         file: null,
                         existingUrl: d.document_file_url || d.document_file,
                         id: d.id,
                         allowReplace: (d.document_type || '').toLowerCase() === 'tax invoice'
                            ? !!initialData.allow_replace_tax_invoice
                            : (d.document_type || '').toLowerCase() === 'receipt'
                                ? !!initialData.allow_replace_receipt
                                : false,
                         // Existing docs are already uploaded; replacement uploads can be re-verified when selected.
                         efdVerified: !(isStage1Approval && isTransporter),
                         // No name field anymore, but keeping for compatibility if backend sends it
                     })));
                }
                
                // Pre-populate job details from the selected job row so the transporter
                // sees IDF, customer and container info immediately, even if the fetch is slow.
                const prefillJob = initialData.job_details
                    ? initialData.job_details
                    : {
                        id: initialData.job || initialData.job_number,
                        idf_no: initialData.idf_number,
                        kk_file_no: initialData.file_no,
                        bl_no: initialData.bl_number,
                        customer: initialData.customer_name,
                        customer_tin: initialData.customer_tin,
                        container_summary: initialData.container_summary,
                        containers_list: Array.isArray(initialData.containers)
                            ? initialData.containers.map(c => ({ number: c.container_no, type: c.container_type, ...c }))
                            : []
                    };
                setJobDetails(prefillJob);
                if (isTransporter) {
                    setJobSearchField('idf_no');
                    setJobSearchTerm(prefillJob.idf_no || '');
                }

                // Fetch Job — prefer job ID (unique) over IDF (may be shared across jobs)
                const jobId = initialData.job || initialData.job_number;
                const idfToFetch = initialData.idf_number || initialData.job_details?.idf_no;
                if (jobId) {
                    setJobSearchField('job_no');
                    setJobSearchTerm(String(jobId));
                    await fetchJobDetails(String(jobId), true, 'job_no');
                } else if (idfToFetch) {
                    setJobSearchField('idf_no');
                    setJobSearchTerm(idfToFetch);
                    await fetchJobDetails(idfToFetch, true, 'idf_no');
                }
            } else {
                // Initial empty item is set via useState lazy initializer
            }
        };
        init();
    }, [initialData]);

    // Calculate Totals
    useEffect(() => {
        const r2 = (n) => Math.round(n * 100) / 100;
        const isReadMode = isView || receiptUploadOnly;

        // In view/receiptUploadOnly mode, display the saved backend totals directly.
        // The foreign total is the saved total_fx, or the TZS grand total converted by the FX rate.
        if (isReadMode && initialData) {
            const fxR = invoiceCurrencyDifferent
                ? (fxRoundOff ? Math.round(parseFloat(invoiceCurrencyData.fx_rate || 1) * 100) / 100 : parseFloat(invoiceCurrencyData.fx_rate || 1))
                : 1;
            const savedGross = parseFloat(initialData.total_amount) || 0;
            const convertedGross = formData.currency === 'USD' ? r2(savedGross * (fxR || 1)) : r2(savedGross / (fxR || 1));

            setNetTotal(parseFloat(initialData.net_amount) || 0);
            setVatTotal(parseFloat(initialData.total_vat) || 0);
            setGrossTotal(savedGross);
            setTotalForeignAmount(initialData.total_fx ? parseFloat(initialData.total_fx) : convertedGross);
            return;
        }

        // Edit/create mode: calculate from items so changes are reflected immediately
        let net = 0;
        let vat = 0;
        let totalFromDisplay = 0;

        const fxRate = invoiceCurrencyDifferent
            ? (fxRoundOff ? Math.round(parseFloat(invoiceCurrencyData.fx_rate || 1) * 100) / 100 : parseFloat(invoiceCurrencyData.fx_rate || 1))
            : 1;

        items.forEach(item => {
            const amount = r2(parseFloat(item.amount) || 0);
            const vatAmount = r2(parseFloat(item.vat_amount) || 0);
            const totalAmount = r2(parseFloat(item.total_amount) || 0);
            const foreignAmount = r2(parseFloat(item.foreign_amount) || 0);
            const isAdjustment = item.charge_name === 'ADJUSTMENT';

            // Effective TZS amount: if currency diff and no foreign_amount saved, amount may be the foreign value
            // (backward compat); otherwise item.amount is already TZS (set by updateItem).
            const effectiveAmount = r2((invoiceCurrencyDifferent && !isAdjustment && !foreignAmount)
                ? amount * fxRate
                : amount);

            // Always derive VAT and total from the current TZS amount and add_vat flag in edit/create mode
            // so toggling VAT or changing values updates the totals immediately.
            const itemVat = item.add_vat ? r2(effectiveAmount * 0.18) : 0;

            if (item.add_vat) {
                vat += itemVat;
            }
            // For ADJUSTMENT: prefer saved total_amount, otherwise use amount
            const adjustmentTZS = isAdjustment
                ? (totalAmount || amount)
                : 0;

            // NetTotal: sum of effectiveAmount per item (ADJUSTMENT uses total_amount or amount)
            net += isAdjustment ? adjustmentTZS : effectiveAmount;

            // GrossTotal: use saved total_amount if available, otherwise recalculate from effectiveAmount
            const itemGross = isAdjustment
                ? adjustmentTZS
                : (totalAmount > 0 ? totalAmount : (item.add_vat ? r2(effectiveAmount + itemVat) : effectiveAmount));
            totalFromDisplay += itemGross;
        });
        setNetTotal(net);
        setVatTotal(vat);
        setGrossTotal(totalFromDisplay);
        // USD TOTAL: TZS grand total converted by the FX rate
        setTotalForeignAmount(invoiceCurrencyDifferent
            ? r2(formData.currency === 'USD' ? totalFromDisplay * fxRate : totalFromDisplay / (fxRate || 1))
            : 0);
    }, [items, formData.control_no_type, invoiceCurrencyDifferent, invoiceCurrencyData.fx_rate, fxRoundOff, initialData]);

    // Validation Effect for Control Number
    useEffect(() => {
        if (!formData.pay_by_control_no && (formData.control_no || formData.control_no_type)) {
            setFormData(prev => ({ ...prev, control_no: '', control_no_type: '' }));
        }
    }, [formData.pay_by_control_no]);

    // Default TISS/TIPS to NO TISS/TIPS for GEPG control numbers and cash payments
    useEffect(() => {
        if ((isGepgPayable || isCash) && stage3Data.tiss_tips !== 'NO TISS/TIPS') {
            setStage3Data(prev => ({ ...prev, tiss_tips: 'NO TISS/TIPS' }));
        }
    }, [isGepgPayable, isCash]);

    // Auto-check "Pay by Control No" when selected vendor has CONTROL NUMBER as a payment mode
    useEffect(() => {
        if (!formData.vendor || isFieldDisabled || (mode !== 'add' && mode !== 'create') || isTransporter) return;
        const selectedVendor = vendorOptions.find(v => String(v.id) === String(formData.vendor));
        if (!selectedVendor) return;
        const modes = (selectedVendor.payment_modes || '').toUpperCase().split(',').map(m => m.trim());
        const hasControlNumber = modes.includes('CONTROL NUMBER');
        setFormData(prev => ({
            ...prev,
            pay_by_control_no: hasControlNumber,
            // Clear control no fields if switching away from a control-number vendor
            control_no: hasControlNumber ? prev.control_no : '',
            control_no_type: hasControlNumber ? prev.control_no_type : '',
        }));
    }, [formData.vendor, vendorOptions]);

    const handleInvoiceTypeChange = (newType) => {
        setFormData(prev => ({
            ...prev,
            invoice_type: newType,
            invoice_no: newType === 'PROFORMA' ? prev.proforma_invoice_no : prev.tax_invoice_no
        }));
    };

    const handleInvoiceNoChange = (val) => {
        setFormData(prev => ({
            ...prev,
            invoice_no: val,
            proforma_invoice_no: prev.invoice_type === 'PROFORMA' ? val : prev.proforma_invoice_no,
            tax_invoice_no: prev.invoice_type === 'TAX' ? val : prev.tax_invoice_no
        }));
    };

    // IDF Search Debounce
    useEffect(() => {
        const timer = setTimeout(() => {
            if (jobSearchField && jobSearchTerm && jobSearchTerm.length > 1 && !jobDetails) {
                 fetchJobDetails(jobSearchTerm, false, jobSearchField);
            }
        }, 600);
        return () => clearTimeout(timer);
    }, [jobSearchTerm, jobSearchField]);

    const fetchJobDetails = async (term, forceSelect = false, searchField = jobSearchField) => {
        setLoadingInfo(true);
        try {
            const q = encodeURIComponent(term || '');
            const sf = encodeURIComponent(searchField || 'idf_no');
            const exactParam = forceSelect ? '&exact=true' : '';
            const res = await fetch(`${API_JOB_SEARCH}?search_field=${sf}&search=${q}${exactParam}`, { headers: getAuthHeaders() });
            if (res.ok) {
                const results = await res.json();
                setIdfOptions(results);
                
                if (forceSelect && results.length > 0) {
                     handleSelectJob(results[0]);
                }
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoadingInfo(false);
        }
    };

    const handleSelectJob = (job) => {
        setJobDetails(job);
        setFormData(prev => ({
            ...prev,
            job: job.id
        }));
        if (isTransporter) {
            setJobSearchField('idf_no');
            setJobSearchTerm(job.idf_no || '');
        } else {
            setJobSearchTerm(getJobSearchValue(job, jobSearchField || 'idf_no'));
        }
        setIdfOptions([]);

        // Parse Container Summary for Unit Options
        // job.containers_list is [{number: '...', type: '20 GP'}, ...]
        if (job.containers_list && Array.isArray(job.containers_list)) {
            const types = [...new Set(job.containers_list.map(c => c.type))];
            if (types.length > 0) {
                setUnitOptions(types.map(t => ({ id: t, text: t })));
            } else {
                 // Fallback if empty
                 setUnitOptions([{id: 'Unit', text: 'Unit'}]);
            }
        } else {
             // Fallback
             setUnitOptions([{id: 'Unit', text: 'Unit'}]);
        }


        const isZnzJob = (job.cargo_type || '').toUpperCase() === 'ZNZ';

        // Auto-attach the right support document from Job Documents if available and not already in list
        if (job.documents && Array.isArray(job.documents)) {
            if (isZnzJob) {
                const commercialDoc = job.documents.find(d => {
                    if (!d.document_type) return false;
                    return d.document_type.toUpperCase().includes('COMMERCIAL INVOICE');
                });

                if (commercialDoc) {
                    setDocuments(prev => {
                        if (prev.some(d => (d.type || '').toUpperCase().includes('COMMERCIAL INVOICE'))) {
                            return prev;
                        }
                        return [
                            ...prev,
                            {
                                type: commercialDoc.document_type,
                                existingUrl: commercialDoc.document_file_url || commercialDoc.document_file,
                                id: null,
                                isJobDocument: true
                            }
                        ];
                    });
                }
            } else {
                const blDoc = job.documents.find(d => {
                    if (!d.document_type) return false;
                    const docTypeUpper = d.document_type.toUpperCase();
                    const matches = ['LADING', 'BL', 'OBL', 'HBL', 'MBL', 'AWB', 'RCN', 'AIRWAY BILL', 'ROAD CONSIGNMENT NOTE'].some(type =>
                        docTypeUpper.includes(type)
                    );
                    return matches;
                });

                if (blDoc) {
                    setDocuments(prev => {
                        // Check if we already have a BL document
                        if (prev.some(d => d.type === 'BL' || (d.type && (['LADING', 'BL', 'OBL', 'HBL', 'MBL', 'AWB', 'RCN', 'AIRWAY BILL', 'ROAD CONSIGNMENT NOTE'].some(type =>
                             d.type.toUpperCase().includes(type)
                    ))))) {
                            return prev;
                        }
                        return [
                            ...prev,
                            {
                                type: blDoc.document_type,
                                existingUrl: blDoc.document_file_url || blDoc.document_file,
                                id: null, // It's not a payable document yet, but we treat it as "existing" for display
                                isJobDocument: true // Flag to know source
                            }
                        ];
                    });
                }
            }

        }

        // PERMIT payables: always add an ASSESSMENT NOTICE document row;
        // link the job's Assessment Notice file automatically when present (like BL)
        if ((module || 'PERMIT') === 'PERMIT') {
            const assessmentDoc = (job.documents || []).find(d =>
                d.document_type && d.document_type.toUpperCase().includes('ASSESSMENT NOTICE')
            ) || (job.documents || []).find(d =>
                d.document_type && d.document_type.toUpperCase().includes('ASSESSMENT')
            );

            setDocuments(prev => {
                if (prev.some(d => d.type && d.type.toUpperCase().includes('ASSESSMENT'))) {
                    return prev;
                }
                return [
                    ...prev,
                    {
                        type: 'ASSESSMENT NOTICE',
                        ...(assessmentDoc ? {
                            existingUrl: assessmentDoc.document_file_url || assessmentDoc.document_file,
                            isJobDocument: true
                        } : {}),
                        id: null
                    }
                ];
            });
        }
    };

    // Item Management
    const addItem = () => {
        if (isTransporter) {
            setItems(prev => [...prev, { charge_name: '', units: 1, unit_type: 'Unit', unit_rate: '', amount: 0, add_vat: false, total_amount: '' }]);
        } else {
            setItems(prev => [...prev, { charge_name: '', units: '', unit_type: '', unit_rate: '', amount: 0, add_vat: false }]);
        }
    };

    const removeItem = (index) => {
        setItems(prev => prev.filter((_, i) => i !== index));
    };

    const updateItem = (index, field, value) => {
        const newItems = [...items];

        if (field === 'charge_name') {
            newItems[index][field] = value;
            // Lookup VAT status
            const charge = chargeOptions.find(c => c.id === value);
            if (charge) {
                newItems[index].add_vat = charge.add_vat;
            }
            // If ADJUSTMENT is selected, set units to 1 and unit_rate to amount
            if (value === 'ADJUSTMENT') {
                newItems[index].units = 1;
                newItems[index].unit_type = '';
                newItems[index].unit_rate = newItems[index].amount || 0;
                newItems[index].add_vat = false;
                setAdjustmentErrors(prev => ({ ...prev, [index]: null }));
            } else {
                setAdjustmentErrors(prev => ({ ...prev, [index]: null }));
            }
        } else if (field === 'amount') {
            // Validate adjustment amount range based on currency
            if (newItems[index].charge_name === 'ADJUSTMENT') {
                const numValue = parseFloat(value);
                const currency = formData.currency;
                let minRange, maxRange;

                if (currency === 'USD') {
                    minRange = -0.50;
                    maxRange = 0.50;
                } else {
                    minRange = -1000;
                    maxRange = 1000;
                }

                if (numValue < minRange || numValue > maxRange) {
                    // Clamp value to range
                    const clampedValue = numValue < minRange ? minRange : maxRange;
                    newItems[index][field] = clampedValue;
                    newItems[index].total_amount = clampedValue;
                    if (invoiceCurrencyDifferent && invoiceCurrencyData.fx_rate) {
                        const rawRate = parseFloat(invoiceCurrencyData.fx_rate);
                        const effectiveRate = fxRoundOff ? Math.round(rawRate * 100) / 100 : rawRate;
                        newItems[index].unit_rate = (clampedValue / effectiveRate).toFixed(2);
                    } else {
                        newItems[index].unit_rate = clampedValue;
                    }
                    setAdjustmentErrors(prev => ({ ...prev, [index]: 'Adjustment amount is beyond the range allowed' }));
                    setItems(newItems);
                    return;
                } else {
                    setAdjustmentErrors(prev => ({ ...prev, [index]: null }));
                }
                // Update unit_rate for ADJUSTMENT
                if (invoiceCurrencyDifferent && invoiceCurrencyData.fx_rate) {
                    const rawRate = parseFloat(invoiceCurrencyData.fx_rate);
                    const effectiveRate = fxRoundOff ? Math.round(rawRate * 100) / 100 : rawRate;
                    newItems[index].unit_rate = (numValue / effectiveRate).toFixed(2);
                } else {
                    newItems[index].unit_rate = numValue;
                }
                newItems[index].total_amount = numValue;
            }
            newItems[index][field] = value;
        } else if (field === 'foreign_amount') {
            // Handle foreign currency amount input
            newItems[index][field] = value; // Save the foreign_amount field
            if (invoiceCurrencyDifferent && invoiceCurrencyData.fx_rate && newItems[index].charge_name !== 'ADJUSTMENT') {
                const numValue = parseFloat(value);
                const rawRate = parseFloat(invoiceCurrencyData.fx_rate);
                const effectiveRate = fxRoundOff ? Math.round(rawRate * 100) / 100 : rawRate;
                const units = parseFloat(newItems[index].units) || 1;
                // unit_rate is the foreign-currency rate per unit
                newItems[index].unit_rate = (numValue / units).toFixed(2);
                // TZS amount is the foreign total converted at the FX rate
                newItems[index].amount = (numValue * effectiveRate).toFixed(2);
            }
        } else {
            newItems[index][field] = value;
        }

        if (field === 'units' || field === 'unit_rate') {
            const units = parseFloat(newItems[index].units) || 0;
            const rate = parseFloat(newItems[index].unit_rate) || 0;
            const isAdjustment = newItems[index].charge_name === 'ADJUSTMENT';
            // Only recalculate if NOT in view mode
            if (!isView && !isAdjustment) {
                if (invoiceCurrencyDifferent && invoiceCurrencyData.fx_rate) {
                    const rawRate = parseFloat(invoiceCurrencyData.fx_rate);
                    const effectiveRate = fxRoundOff ? Math.round(rawRate * 100) / 100 : rawRate;
                    // Keep foreign_amount in sync with units * foreign rate
                    newItems[index].foreign_amount = (units * rate).toFixed(2);
                    // TZS amount is the foreign total converted at the FX rate
                    newItems[index].amount = (units * rate * effectiveRate).toFixed(2);
                } else {
                    newItems[index].amount = (units * rate).toFixed(2);
                    newItems[index].foreign_amount = '';
                }
            }
        }

        // Recalculate VAT and total so the UI reflects any change immediately
        if (!isView) {
            newItems[index] = recalculateItemAmounts(newItems[index], invoiceCurrencyDifferent, invoiceCurrencyData, fxRoundOff, isTransporter);
        }

        setItems(newItems);
    };

    // Update items when FX Rate or Round Off setting changes
    // DISABLED: No recalculations when viewing payables - all data should be retrieved from database as-is
    // useEffect(() => {
    //     if (invoiceCurrencyDifferent && invoiceCurrencyData.fx_rate) {
    //         const rawRate = parseFloat(invoiceCurrencyData.fx_rate);
    //         const effectiveRate = fxRoundOff ? Math.round(rawRate) : rawRate;
    //         setItems(prev => prev.map(item => {
    //             if (item.charge_name === 'ADJUSTMENT') {
    //                 return item; // Don't recalculate amount for ADJUSTMENT
    //             }
    //             const units = parseFloat(item.units) || 0;
    //             const rate = parseFloat(item.unit_rate) || 0;
    //             const newAmount = (units * rate * effectiveRate).toFixed(2);
    //             return { ...item, amount: newAmount };
    //         }));
    //     }
    // }, [invoiceCurrencyData.fx_rate, invoiceCurrencyDifferent, fxRoundOff]);
    
    // Toggle VAT
    const toggleItemVat = (index) => {
        const newItems = [...items];
        newItems[index].add_vat = !newItems[index].add_vat;
        if (!isView) {
            newItems[index] = recalculateItemAmounts(newItems[index], invoiceCurrencyDifferent, invoiceCurrencyData, fxRoundOff, isTransporter);
        }
        setItems(newItems);
    }

    // Document Management
    const addDocument = () => {
        setDocuments(prev => [...prev, { type: isTransporter ? 'TAX INVOICE' : '', file: null }]);
    };

    const removeDocument = (index) => {
        const doc = documents[index];
        if (doc && doc.id) {
            setDocIdsToDelete(prev => [...prev, doc.id]);
        }
        setDocuments(prev => prev.filter((_, i) => i !== index));
    };

    const updateDocument = (index, field, value) => {
        const newDocs = [...documents];
        newDocs[index][field] = value;
        setDocuments(newDocs);
    };

    const handleReplaceDocumentSave = async (idx) => {
        const doc = documents[idx];
        if (!doc || !doc.file) {
            notifyError('Please select a new document file first.');
            return;
        }
        if (!initialData?.id) {
            notifyError('No payable loaded.');
            return;
        }

        setSavingDocIndex(idx);
        try {
            const fd = new FormData();
            fd.append('document_id', doc.id || '');
            fd.append('document_type', doc.type || '');
            fd.append('document_file', doc.file);

            const res = await fetch(`${API_PAYABLES}${initialData.id}/replace-document/`, {
                method: 'POST',
                headers: getAuthHeaders(true),
                body: fd,
            });

            if (!res.ok) {
                const errData = await res.json().catch(() => ({}));
                notifyError(errData.detail || 'Failed to replace document.');
                return;
            }

            const data = await res.json();
            const newDocs = [...documents];
            newDocs[idx] = {
                ...newDocs[idx],
                id: data.id || newDocs[idx].id,
                existingUrl: data.document_file_url || data.document_file || newDocs[idx].existingUrl,
                file: null,
                type: data.document_type || newDocs[idx].type,
            };
            setDocuments(newDocs);
            notifySuccess('Document replaced and saved successfully.');
        } catch (err) {
            console.error(err);
            notifyError('Error replacing document.');
        } finally {
            setSavingDocIndex(null);
        }
    };

    const handleApprove = () => {
        if (isStage1Approval && isTransporter && formData.control_no_type !== 'GEPG') {
            const efdRequiredDocs = documents.filter(d =>
                ['tax invoice', 'receipt'].includes((d.type || '').toLowerCase()) && (d.file || d.existingUrl)
            );
            const hasAnyEfdVerified = efdRequiredDocs.some(d => !!d.efdVerified);
            if (efdRequiredDocs.length > 0 && !hasAnyEfdVerified) {
                notifyError('Please check EFD VERIFIED for at least one document (Tax Invoice or Receipt) before approving.');
                return;
            }
        }
        onApprove();
    };

    const renderApprovalContent = () => {
        const isReadOnlyPayment = isStage3Approval || isExpensesView || (mode === 'view' && !approvalMode);
        const showPaymentSection = !isTransporterPortal && (isStage2Approval || isStage3Approval || isExpensesView || ['APPROVED 2', 'APPROVED 3', 'CLIENT PAID', 'CLIENT_PAID', 'COMPLETED'].includes(initialData.status));

        const createdByEntry = (initialData?.created_by_name || (isTransporter && initialData?.transporter_name)) && initialData?.created_at ? [{
            key: 'created-by',
            label: 'CREATED BY:',
            by: isTransporter ? (initialData.created_by_name || initialData.transporter_name) : initialData.created_by_name,
            at: initialData.created_at,
            isCreated: true,
        }] : [];
        const approvalHistoryEntries = (initialData?.approval_history?.length ?
            initialData.approval_history
            .filter(event => !['REJECTED', 'REJECTION'].includes(String(event.event_type || '').toUpperCase()))
            .map((event, idx) => ({
                key: `event-${event.id || idx}`,
                label:
                    event.event_type === 'STAGE_1_APPROVED' ? 'STAGE 1 APPROVED:' :
                    event.event_type === 'STAGE_2_APPROVED' ? 'STAGE 2 APPROVED:' :
                    event.event_type === 'STAGE_3_APPROVED' ? 'STAGE 3 APPROVED:' :
                    event.event_type === 'RETURNED_TO_STAGE_3' ? 'RETURNED TO STAGE 3:' :
                    event.event_type === 'RETURNED_TO_STAGE_2' ? 'RETURNED TO STAGE 2:' :
                    event.event_type === 'RETURNED_TO_STAGE_1' ? 'RETURNED TO STAGE 1:' :
                    `${String(event.event_type || '').replace(/_/g, ' ')}:`,
                by: event.acted_by_name,
                at: event.event_at,
            })) :
            (initialData ? [
            {
                key: 'stage1-approved',
                label: 'STAGE 1 APPROVED:',
                by: initialData.approved_stage_1_by_name,
                at: initialData.approved_stage_1_at,
            },
            {
                key: 'stage2-approved',
                label: 'STAGE 2 APPROVED:',
                by: initialData.approved_stage_2_by_name,
                at: initialData.approved_stage_2_at,
            },
            {
                key: 'stage3-approved',
                label: 'STAGE 3 APPROVED:',
                by: initialData.approved_stage_3_by_name,
                at: initialData.approved_stage_3_at,
            },
            {
                key: 'returned-stage3',
                label: 'RETURNED TO STAGE 3:',
                by: initialData.returned_to_stage_3_by_name,
                at: initialData.returned_to_stage_3_at,
            },
            {
                key: 'client-paid',
                label: 'CLIENT PAID:',
                by: initialData.action_by_name,
                at: initialData.action_date,
            },
        ] : []))
            .map((entry) => ({ ...entry, timestamp: Date.parse(entry.at) }))
            .filter((entry) => entry.by && entry.at && !Number.isNaN(entry.timestamp))
            .sort((a, b) => a.timestamp - b.timestamp);
        const rejectionEntries = (initialData?.rejections || []).map((r, idx) => ({
            key: `rejection-${r.id || idx}`,
            label: 'REJECTED:',
            by: r.rejected_by_name,
            at: r.rejection_date,
            reason: r.reason,
            isRejection: true,
            timestamp: Date.parse(r.rejection_date),
        }));
        const allHistoryEntries = [
            ...createdByEntry,
            ...[...approvalHistoryEntries, ...rejectionEntries].sort((a, b) => a.timestamp - b.timestamp)
        ];

        let containerList = [];
        if (isTransporter && initialData?.containers && initialData.containers.length > 0) {
            containerList = initialData.containers;
        } else if (jobDetails?.containers_list && jobDetails.containers_list.length > 0) {
            containerList = jobDetails.containers_list;
        } else if (jobDetails?.containers && jobDetails.containers.length > 0) {
            containerList = jobDetails.containers;
        }
        const containerListString = containerList
            .map(c => c.container_no || c.number || '')
            .filter(Boolean)
            .join(' | ');

        return (
        <>
            {/* Approval History Display */}
            {initialData && !isTransporterPortal && (
                <div style={{ marginBottom: '8px', padding: '6px', background: '#f8f9fa', borderRadius: '5px', border: '1px solid #dee2e6' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', borderBottom: '1px solid #dee2e6', marginBottom: '5px', paddingBottom: '3px' }}>
                        <button
                            type="button"
                            className="icon-btn"
                            onClick={() => setShowApprovalHistory(prev => !prev)}
                            title={showApprovalHistory ? 'Collapse Approval History' : 'Expand Approval History'}
                            style={{ color: '#555' }}
                        >
                            <i className={`fa ${showApprovalHistory ? 'fa-chevron-down' : 'fa-chevron-right'}`}></i>
                        </button>
                        <div style={{ fontWeight: 'bold' }}>APPROVAL HISTORY</div>
                    </div>
                    {showApprovalHistory && (
                    <>
                    <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #dee2e6', marginTop: '5px' }}>
                        <thead>
                            <tr style={{ background: '#e9ecef' }}>
                                <th style={{ border: '1px solid #dee2e6', padding: '6px 8px', textAlign: 'left', fontWeight: 'bold' }}>EVENT</th>
                                <th style={{ border: '1px solid #dee2e6', padding: '6px 8px', textAlign: 'left', fontWeight: 'bold' }}>USER</th>
                                <th style={{ border: '1px solid #dee2e6', padding: '6px 8px', textAlign: 'center', fontWeight: 'bold' }}>DATE</th>
                                <th style={{ border: '1px solid #dee2e6', padding: '6px 8px', textAlign: 'left', fontWeight: 'bold' }}>REJECTION REASON</th>
                            </tr>
                        </thead>
                        <tbody>
                            {allHistoryEntries.map((entry) => (
                                <tr key={entry.key}
                                    onMouseEnter={e => e.currentTarget.style.background = '#eef2ff'}
                                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                >
                                    <td style={{ border: '1px solid #dee2e6', padding: '6px 8px', fontWeight: 'bold', color: entry.isCreated ? '#444' : (entry.isRejection ? '#c0392b' : '#666'), whiteSpace: 'nowrap' }}>{entry.label}</td>
                                    <td style={{ border: '1px solid #dee2e6', padding: '6px 8px', textAlign: 'left', whiteSpace: 'nowrap' }}>{entry.by}</td>
                                    <td style={{ border: '1px solid #dee2e6', padding: '6px 8px', textAlign: 'center', color: '#888', whiteSpace: 'nowrap' }}>{formatDateTimeDisplay(entry.at)}</td>
                                    <td style={{ border: '1px solid #dee2e6', padding: '6px 8px', textAlign: 'left', color: 'red', whiteSpace: 'pre-wrap' }}>{entry.reason || ''}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {/* Added section for CLIENT PAID */}
                    {(initialData.status === 'CLIENT PAID' || initialData.status === 'CLIENT_PAID') && (
                         <div style={{ padding: '10px', background: '#d4edda', color: '#155724', fontWeight: 'bold', marginTop: '10px', textAlign: 'center', borderRadius: '4px', border: '1px solid #c3e6cb' }}>
                             PAYMENT STATUS: CLIENT PAID
                         </div>
                    )}
                    </>
                    )}
                </div>
            )}
            
            <style>
                {`
                    @keyframes blink-yellow {
                        0% { background-color: transparent; }
                        50% { background-color: yellow; }
                        100% { background-color: transparent; }
                    }
                    .blink-bg {
                        animation: blink-yellow 1s infinite;
                        padding: 2px 5px;
                        border-radius: 3px;
                        display: inline-block;
                    }
                    @keyframes flash-amount {
                        0%, 100% { opacity: 1; }
                        50% { opacity: 0.15; }
                    }
                    .flash-amount {
                        animation: flash-amount 1s infinite;
                    }
                `}
            </style>
            {/* Read-Only Job Details using Styled Text */}
            <div className="form-section" style={{ backgroundColor: '#e8f5e9', padding: '0 8px 8px 8px', borderRadius: '5px', border: '1px solid #c8e6c9' }}>
                <h4 style={{ color: '#2e7d32', borderBottom: '1px solid #a5d6a7', paddingBottom: '5px', marginTop: 0 }}>JOB & INVOICE DETAILS</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px' }}>
                    
                    <div>
                        <small style={{ color: '#666' }}>JOB ID</small>
                        <div style={{ fontWeight: 'bold' }}>{jobDetails?.id || initialData?.job_number || ''}</div>
                    </div>
                    <div>
                        <small style={{ color: '#666' }}>FILE NO</small>
                        <div style={{ fontWeight: 'bold' }}>{jobDetails?.kk_file_no || initialData?.file_no || ''}</div>
                    </div>
                    <div>
                        <small style={{ color: '#666' }}>IDF NO</small>
                        <div style={{ fontWeight: 'bold' }}>{jobDetails?.idf_no || initialData?.idf_number || ''}</div>
                    </div>
                    <div>
                        <small style={{ color: '#666' }}>CUSTOMER</small>
                        <div style={{ fontWeight: 'bold' }}>{jobDetails?.customer || initialData?.customer_name || ''}</div>
                    </div>
                    <div>
                        {(initialData?.customer_tin || jobDetails?.customer_tin) && (
                            <>
                            <small style={{ color: '#666' }}>CUSTOMER TIN</small>
                            <div style={{ fontWeight: 'bold' }}>{jobDetails?.customer_tin || initialData?.customer_tin || ''}</div>
                            </>
                        )}
                    </div>
                    <div>
                         <small style={{ color: '#666' }}>VENDOR</small>
                         <div style={{ fontWeight: 'bold' }}>{initialData.vendor_name || formData.vendor || ''}</div>
                    </div>

                    <div>
                         <small style={{ color: '#666' }}>INVOICE TYPE</small>
                         <div className={initialData.status === 'PENDING' ? "blink-bg" : ""} style={{ fontWeight: 'bold', fontSize: '1.1em', marginTop: '5px', display: 'block' }}>{initialData.invoice_type || formData.invoice_type || ''}</div>
                    </div>
                    
                    <div>
                        <small style={{ color: '#666' }}>INVOICE NO</small>
                        <div style={{ fontWeight: 'bold' }}>{initialData.invoice_no || formData.invoice_no || ''}</div>
                    </div>
                    <div>
                        <small style={{ color: '#666' }}>INVOICE DATE</small>
                        <div style={{ fontWeight: 'bold' }}>{formatDateDisplay(initialData.invoice_date || formData.invoice_date || '')}</div>
                    </div>
                    <div>
                        <small style={{ color: '#666' }}>{(jobDetails?.hbl_so_no || initialData?.hbl_so_no) ? 'HBL NUMBER' : 'BL NUMBER'}</small>
                        <div style={{ fontWeight: 'bold' }}>{jobDetails?.bl_no || initialData?.bl_number || ''}</div>
                    </div>

                    <div style={{ gridColumn: 'span 2' }}>
                        <small style={{ color: '#666' }}>CONTAINERS</small>
                        <div style={{ fontWeight: 'bold' }}>
                            {jobDetails?.container_summary || initialData?.container_summary || '-'}
                            {containerListString && (
                                <span style={{ color: '#c0392b', marginLeft: 10 }}>| {containerListString}</span>
                            )}
                        </div>
                    </div>

                    <div style={{ gridColumn: 'span 2' }}></div>

                    <div style={{ gridColumn: 'span 5', display: 'flex', gap: '20px', alignItems: 'center', background: '#fff3e0', padding: '5px 8px', borderRadius: '4px', border: '1px solid #ffe0b2' }}>
                        <div>
                             <small style={{ color: '#666', fontWeight: 'bold' }}>CURRENCY</small>
                             <div style={{ fontWeight: 'bold', color: 'red', fontSize: '1.2em' }}>{formData.currency}</div>
                        </div>
                        <div>
                             <small style={{ color: '#666', fontWeight: 'bold' }}>AMOUNT</small>
                             <div className="flash-amount" style={{ fontWeight: 'bold', color: 'red', fontSize: '1.4em' }}>
                                {formatCurrency(
                                   // invoiceCurrencyDifferent && items.some(item => !item.foreign_amount || item.foreign_amount === 0) && items.some(item => !item.total_amount) && invoiceCurrencyData && invoiceCurrencyData.fx_rate
                                   //     ? (isExpensesView && isTissOrTipsSelected ? stage3NewPayableAmount : grossTotal) * (fxRoundOff ? Math.round(parseFloat(invoiceCurrencyData.fx_rate)) : parseFloat(invoiceCurrencyData.fx_rate))
                                   //     : (isExpensesView && isTissOrTipsSelected ? stage3NewPayableAmount : grossTotal)
                                   isExpensesView && !hideClientPaid ? (grossTotal + parseFloat(initialData?.tiss_tips_amount || 0) + parseFloat(initialData?.gepg_amount || 0)) : grossTotal
                                )}
                             </div> 
                        </div>
                        {invoiceCurrencyDifferent && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 'bold' }}>
                             <span>|</span>
                             <div style={{ color: 'green', lineHeight: 1.1, textAlign: 'center' }}>
                                <div>DIFF</div>
                                <div>TOOL</div>
                             </div>
                             <span style={{ fontSize: '1.3em' }}>→</span>
                             <span>|</span>
                        </div>
                        )}
                        {invoiceCurrencyDifferent && invoiceCurrencyData?.total_tzs && (
                        <div>
                             <small style={{ color: '#666', fontWeight: 'bold' }}>TZS AMOUNT</small>
                             <div style={{ fontWeight: 'bold', color: 'red', fontSize: '1.2em' }}>{formatCurrency(invoiceCurrencyData.total_tzs)}</div>
                        </div>
                        )}
                        {invoiceCurrencyDifferent && invoiceCurrencyData?.total_fx && (
                        <div>
                             <small style={{ color: '#666', fontWeight: 'bold' }}>USD AMOUNT</small>
                             <div style={{ fontWeight: 'bold', color: 'red', fontSize: '1.2em' }}>{formatCurrency(invoiceCurrencyData.total_fx)}</div>
                        </div>
                        )}
                        <div>
                             <small style={{ color: '#666', fontWeight: 'bold' }}>EXCH RATE</small>
                             <div style={{ fontWeight: 'bold', color: 'red', fontSize: '1.2em' }}>{formData.usd_rate}</div>
                        </div>
                    </div>

                    <div style={{ gridColumn: 'span 5' }}>
                        <small style={{ color: '#666' }}>REMARKS</small>
                        <div style={{ fontWeight: 'bold', color: 'blue', textTransform: 'uppercase' }}>{initialData.remarks || formData.remarks || '-'}</div>
                    </div>
                </div>
            </div>

            {/* Control Number View (Stage 1 / Stage 2 / View mode for OTHER) */}
            {formData.pay_by_control_no && (isView && !isStage3Approval && !isExpensesView) && (
                <div style={{ padding: '15px', background: '#e9ecef', borderRadius: '4px', marginBottom: '15px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
                        <div>
                            <label style={{ display: 'block', fontWeight: 'bold', fontSize: '11px', color: '#888' }}>CONTROL NUMBER</label>
                            <div style={{ fontWeight: 'bold', color: 'red', fontSize: '16px' }}>{formData.control_no}</div>
                        </div>
                        <div>
                            <label style={{ display: 'block', fontWeight: 'bold', fontSize: '11px', color: '#888' }}>CONTROL NUMBER TYPE</label>
                            <div style={{ fontWeight: 'bold' }}>{formData.control_no_type || '-'}</div>
                        </div>
                    </div>
                </div>
            )}

            {/* Payment Details Input - Render only if we are past Stage 1 (Approving 1 -> 2, or Higher) */}
            {showPaymentSection && (
            <div className="form-section" style={{ border: '1px solid #007bff', background: '#f0f7ff', padding: '0 8px 8px 8px' }}>
                <h4 style={{ color: '#007bff', marginTop: 0 }}>{isReadOnlyPayment && !isStage2Approval ? 'PAYMENT DETAILS' : 'PAYMENT PROCESSING'}</h4>
                
                {/* Control Number View (Special Layout) */}
                {formData.pay_by_control_no && (isStage3Approval || isExpensesView) ? (
                    isExpensesView ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: '4px 8px', fontWeight: 'bold' }}>
                            <div>
                                <label style={{ color: '#888', fontSize: '11px', display: 'block' }}>CONTROL NUMBER</label>
                                <div style={{ fontSize: '16px', color: 'red' }}>{formData.control_no}</div>
                            </div>
                            <div>
                                <label style={{ color: '#888', fontSize: '11px', display: 'block' }}>PAID FROM</label>
                                <div>
                                    {[{id:'CASH', text:'CASH'}, ...filteredBankOptions].find(b => b.id === (stage3Data.payment_source || initialData.bank_account))?.text || stage3Data.payment_source || initialData.bank_account || '-'}
                                </div>
                            </div>
                            <div>
                                <label style={{ color: '#888', fontSize: '11px', display: 'block' }}>PAYMENT DATE</label>
                                <div>{formatDateDisplay(stage3Data.payment_date)}</div>
                            </div>
                            <div>
                                <label style={{ color: '#888', fontSize: '11px', display: 'block' }}>TISS / TIPS</label>
                                <div>{stage3Data.tiss_tips || '-'}</div>
                            </div>
                            {isTissOrTipsSelected && (
                                <div>
                                    <label style={{ color: '#888', fontSize: '11px', display: 'block' }}>TISS/TIPS AMOUNT (TZS)</label>
                                    <div style={{ color: '#c0392b' }}>{formatCurrency(isExpensesView ? parseFloat(initialData?.tiss_tips_amount || 0) : stage3TissTipsAmount)}</div>
                                </div>
                            )}
                            {isGepgPayable && parseFloat(isExpensesView ? initialData?.gepg_amount : gepgAmount) > 0 && (
                                <div>
                                    <label style={{ color: '#888', fontSize: '11px', display: 'block' }}>GEPG CHARGE (TZS)</label>
                                    <div style={{ color: '#c0392b' }}>{formatCurrency(isExpensesView ? parseFloat(initialData?.gepg_amount || 0) : gepgAmount)}</div>
                                </div>
                            )}
                        </div>
                    ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                        <div>
                            <label style={{ fontWeight: 'bold' }}>CONTROL NUMBER</label>
                            <div style={{ fontWeight: 'bold', padding: '6px 0', fontSize: '16px', color: 'red' }}>{formData.control_no}</div>
                        </div>
                        
                        <div>
                            <label style={{ fontWeight: 'bold' }}>PAID FROM</label>
                            <Select2Dropdown
                                options={[{ id: 'CASH', text: 'CASH' }, ...filteredBankOptions]}
                                value={stage3Data.payment_source || initialData.bank_account || ''}
                                onChange={(val) => setStage3Data({...stage3Data, payment_source: val})}
                                valueKey="id"
                                displayKey="text"
                                placeholder="SELECT SOURCE"
                                isDisabled={isExpensesView}
                                menuPlacement="auto"
                            />
                        </div>
                    </div>
                   )
                ) : (
                    // Regular Payment Layout
                    <>
                    {isReadOnlyPayment ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: '4px 8px', fontWeight: 'bold' }}>
                            <div>
                                <small style={{ color: '#888', fontSize: '11px' }}>METHOD:</small>
                                <div>{bankOptions.find(b => b.id == stage2Data.bank)?.text || stage2Data.bank || '-'}</div>
                            </div>
                            <div>
                                <small style={{ color: '#888', fontSize: '11px' }}>CHEQUE/REF:</small>
                                <div style={{ color: 'red', fontSize: '16px' }}>{stage2Data.cheque_no || '-'}</div>
                            </div>
                            <div>
                                <small style={{ color: '#888', fontSize: '11px' }}>COLLECTED BY:</small>
                                <div>{(stage2Data.collected_by || '').toUpperCase() || '-'}</div>
                            </div>
                            <div>
                                <small style={{ color: '#888', fontSize: '11px' }}>PAID BY:</small>
                                <div>
                                    {(initialData.status === 'CLIENT PAID' || initialData.status === 'CLIENT_PAID')
                                        ? 'Paid by Client'
                                        : stage2Data.paid_by === 'PAID BOC'
                                            ? 'PAID ON BEHALF OF CLIENT'
                                            : (stage2Data.paid_by === 'K & K' ? 'K AND K' : stage2Data.paid_by) || '-'}
                                </div>
                            </div>
                            {isExpensesView && (<>
                                <div>
                                    <small style={{ color: '#888', fontSize: '11px' }}>PAYMENT DATE:</small>
                                    <div>{formatDateDisplay(stage3Data.payment_date)}</div>
                                </div>
                                <div>
                                    <small style={{ color: '#888', fontSize: '11px' }}>TISS / TIPS:</small>
                                    <div>{stage3Data.tiss_tips || '-'}</div>
                                </div>
                                {isTissOrTipsSelected && (
                                    <div>
                                        <small style={{ color: '#888', fontSize: '11px' }}>TISS/TIPS AMOUNT (TZS):</small>
                                        <div style={{ color: '#c0392b' }}>{formatCurrency(isExpensesView ? parseFloat(initialData?.tiss_tips_amount || 0) : stage3TissTipsAmount)}</div>
                                    </div>
                                )}
                                {isGepgPayable && parseFloat(isExpensesView ? initialData?.gepg_amount : gepgAmount) > 0 && (
                                    <div>
                                        <small style={{ color: '#888', fontSize: '11px' }}>GEPG CHARGE (TZS):</small>
                                        <div style={{ color: '#c0392b' }}>{formatCurrency(isExpensesView ? parseFloat(initialData?.gepg_amount || 0) : gepgAmount)}</div>
                                    </div>
                                )}
                            </>)}
                        </div>
                    ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '8px', alignItems: 'end' }}>
                        <div>
                            <label className="required">PAYMENT METHOD (BANK/CASH)</label>
                            <Select2Dropdown
                                options={[{ id: 'CASH', text: 'CASH', currency: formData.currency || initialData?.currency || 'TZS' }, ...filteredBankOptions]}
                                value={stage2Data.bank}
                                onChange={(val) => {
                                    // If switching to CASH, clear cheque no
                                    setStage2Data(prev => ({ ...prev, bank: val, cheque_no: val === 'CASH' ? '' : prev.cheque_no }));
                                }}
                                valueKey="id"
                                displayKey="text"
                                placeholder="SELECT METHOD"
                            />
                        </div>

                        {stage2Data.bank !== 'CASH' && (
                            <div>
                                <label className="required">CHEQUE / REF NO.</label>
                                <input 
                                    className="form-control"
                                    value={stage2Data.cheque_no}
                                    onChange={(e) => setStage2Data({...stage2Data, cheque_no: e.target.value})}
                                    placeholder="ENTER CHEQUE NO"
                                    maxLength={200}
                                />
                            </div>
                        )}

                        <div>
                            <label className="required">COLLECTED BY / PAID TO</label>
                            <input 
                                className="form-control"
                                value={stage2Data.collected_by}
                                onChange={(e) => setStage2Data({...stage2Data, collected_by: e.target.value.toUpperCase()})}
                                placeholder="ENTER NAME"
                            />
                        </div>
                        
                        <div>
                            <label className="required">PAID BY</label>
                            <Select2Dropdown
                                options={[
                                    { id: 'K & K', text: 'K AND K' },
                                    { id: 'PAID BOC', text: 'PAID ON BEHALF OF CLIENT' }
                                ]}
                                value={stage2Data.paid_by}
                                onChange={(val) => setStage2Data({...stage2Data, paid_by: val})}
                                valueKey="id"
                                displayKey="text"
                                placeholder="SELECT PAYER"
                            />
                        </div>
                    </div>
                    )}
                    
                    {/* Stage 3 Exclusive Inputs (Regular Flow) */}
                    {isStage3Approval && !formData.pay_by_control_no && (<></>)}
                    </>
                )}
            </div>
            )}
            
            <div className="form-section">
                <h4>STATEMENT OF CHARGES</h4>
                <table className="table table-bordered table-sm">
                    <thead style={{ background: '#e9ecef' }}>
                        <tr>
                            <th style={{ textAlign: 'left' }}>CHARGE NAME</th>
                            <th style={{ textAlign: 'left' }}>UNITS</th>
                            <th style={{ textAlign: 'left' }}>RATE</th>
                            {invoiceCurrencyDifferent && <th style={{ textAlign: 'right' }}>{invoiceCurrencyData.currency} AMOUNT</th>}
                            {invoiceCurrencyDifferent && <th style={{ textAlign: 'right' }}>{invoiceCurrencyData.currency} VAT</th>}
                            {invoiceCurrencyDifferent && <th style={{ textAlign: 'right' }}>{invoiceCurrencyData.currency} TOTAL</th>}
                            <th style={{ textAlign: 'right' }}>AMOUNT</th>
                            <th style={{ textAlign: 'right' }}>VAT</th>
                            <th style={{ textAlign: 'right' }}>TOTAL</th>
                        </tr>
                    </thead>
                    <tbody>
                         {items.map((item, idx) => (
                            <tr key={idx}
                                onMouseEnter={e => e.currentTarget.style.background = '#eef2ff'}
                                onMouseLeave={e => e.currentTarget.style.background = ''}
                            >
                                <td style={{ textAlign: 'left' }}>{String(chargeOptions.find(c => (c.id || '').toUpperCase() === (item.charge_name || '').toUpperCase())?.text || item.charge_name || '').toUpperCase()}</td>
                                <td style={{ textAlign: 'left' }}>{`${item.units || ''} ${item.unit_type || ''}`.trim().toUpperCase()}</td>
                                {item.charge_name === 'ADJUSTMENT' ? (
                                    <td style={{ textAlign: 'left' }}></td>
                                ) : (
                                    <td style={{ textAlign: 'left' }}>{formatCurrency(item.unit_rate)}</td>
                                )}
                                {invoiceCurrencyDifferent && (
                                    <td style={{ textAlign: 'right', fontWeight: 'bold' }}>
                                        {item.charge_name !== 'ADJUSTMENT' && formatCurrency(itemForeignAmount(item))}
                                    </td>
                                )}
                                {invoiceCurrencyDifferent && (
                                    <td style={{ textAlign: 'right' }}>
                                        {item.charge_name !== 'ADJUSTMENT' && formatCurrency(itemForeignVat(item))}
                                    </td>
                                )}
                                {invoiceCurrencyDifferent && (
                                    <td style={{ textAlign: 'right', fontWeight: 'bold' }}>
                                        {item.charge_name !== 'ADJUSTMENT' && formatCurrency(itemForeignTotal(item))}
                                    </td>
                                )}
                                <td style={{ textAlign: 'right', fontWeight: 'bold' }}>
                                    {formatCurrency(
                                        item.charge_name === 'ADJUSTMENT'
                                            ? (item.total_amount || item.amount)
                                            : invoiceCurrencyDifferent
                                                ? (
                                                    item.foreign_amount
                                                        ? item.amount
                                                        : (invoiceCurrencyData && invoiceCurrencyData.fx_rate
                                                            ? (item.amount * (fxRoundOff ? Math.round(parseFloat(invoiceCurrencyData.fx_rate) * 100) / 100 : parseFloat(invoiceCurrencyData.fx_rate)))
                                                            : item.amount)
                                                )
                                                : item.amount
                                    )}
                                </td>
                                <td style={{ textAlign: 'right' }}>{formatCurrency(item.vat_amount)}</td>
                                <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{formatCurrency(item.total_amount)}</td>
                            </tr>
                         ))}
                    </tbody>
                    <tfoot>
                         {isExpensesView && !hideClientPaid && isTissOrTipsSelected && (
                             <tr>
                                 <td colSpan="3" style={{ textAlign: 'right' }}><strong>BANK CHARGES ({stage3Data.tiss_tips}) (TZS):</strong></td>
                                 {invoiceCurrencyDifferent && <><td></td><td></td><td></td></>}
                                 <td></td>
                                 <td></td>
                                 <td style={{ textAlign: 'right', color: '#c0392b' }}><strong>{formatCurrency(parseFloat(initialData?.tiss_tips_amount || 0))}</strong></td>
                             </tr>
                         )}
                         {isExpensesView && !hideClientPaid && isGepgPayable && parseFloat(initialData?.gepg_amount || 0) > 0 && (
                             <tr>
                                 <td colSpan="3" style={{ textAlign: 'right' }}><strong>BANK CHARGES (GEPG) (TZS):</strong></td>
                                 {invoiceCurrencyDifferent && <><td></td><td></td><td></td></>}
                                 <td></td>
                                 <td></td>
                                 <td style={{ textAlign: 'right', color: '#c0392b' }}><strong>{formatCurrency(parseFloat(initialData?.gepg_amount || 0))}</strong></td>
                             </tr>
                         )}
                         <tr>
                             <td colSpan="3" style={{textAlign: 'right'}}><strong>{isExpensesView && isTissOrTipsSelected ? `TOTAL EXPENSE (${formData.currency}):` : isExpensesView && isGepgPayable && parseFloat(initialData?.gepg_amount || 0) > 0 ? `TOTAL EXPENSE (${formData.currency}):` : `TOTAL PAYABLE (${formData.currency}):`}</strong></td>
                             {invoiceCurrencyDifferent && (
                                 <td style={{textAlign: 'right', fontWeight: 'bold'}}>
                                    {formatCurrency(items.reduce((s, i) => s + (i.charge_name === 'ADJUSTMENT' ? 0 : itemForeignAmount(i)), 0))}
                                 </td>
                             )}
                             {invoiceCurrencyDifferent && (
                                 <td style={{textAlign: 'right', fontWeight: 'bold'}}>
                                    {formatCurrency(items.reduce((s, i) => s + itemForeignVat(i), 0))}
                                 </td>
                             )}
                             {invoiceCurrencyDifferent && (
                                 <td style={{textAlign: 'right', fontWeight: 'bold'}}>
                                    {formatCurrency(totalForeignAmount)}
                                 </td>
                             )}
                             <td style={{textAlign: 'right', fontWeight: 'bold'}}>
                                {formatCurrency(
                                  //  invoiceCurrencyDifferent && items.some(item => !item.foreign_amount || item.foreign_amount === 0 || !item.total_amount) && invoiceCurrencyData && invoiceCurrencyData.fx_rate
                                  //      ? netTotal * (fxRoundOff ? Math.round(parseFloat(invoiceCurrencyData.fx_rate)) : parseFloat(invoiceCurrencyData.fx_rate))
                                  //      : netTotal
                                  netTotal
                                )}
                             </td>
                             <td style={{textAlign: 'right', fontWeight: 'bold'}}>
                                {formatCurrency(
                                  //  invoiceCurrencyDifferent && items.some(item => !item.foreign_amount || item.foreign_amount === 0 || !item.total_vat) && invoiceCurrencyData && invoiceCurrencyData.fx_rate
                                  //      ? vatTotal * (fxRoundOff ? Math.round(parseFloat(invoiceCurrencyData.fx_rate)) : parseFloat(invoiceCurrencyData.fx_rate))
                                  //      : vatTotal
                                  vatTotal
                                )}
                             </td>
                             <td className="flash-amount" style={{textAlign: 'right', fontSize: '1.1em', color: '#004085', fontWeight: 'bold'}}>
                                {formatCurrency(
                                    //invoiceCurrencyDifferent && items.some(item => !item.foreign_amount || item.foreign_amount === 0) && items.some(item => !item.total_amount) && invoiceCurrencyData && invoiceCurrencyData.fx_rate
                                    //    ? (isExpensesView && isTissOrTipsSelected ? stage3NewPayableAmount : grossTotal) * (fxRoundOff ? Math.round(parseFloat(invoiceCurrencyData.fx_rate)) : parseFloat(invoiceCurrencyData.fx_rate))
                                    //    : (isExpensesView && isTissOrTipsSelected ? stage3NewPayableAmount : grossTotal)
                                    isExpensesView && !hideClientPaid ? (grossTotal + parseFloat(initialData?.tiss_tips_amount || 0) + parseFloat(initialData?.gepg_amount || 0)) : grossTotal
                                )}
                             </td>
                         </tr>
                    </tfoot>
                </table>
            </div>

             {/* Cheque Details Section (Stage 3+) */}
             {(isStage3Approval || isExpensesView) && chequePayables.length > 0 && (
                <div className="form-section">
                    <h4>CHEQUE DETAILS</h4>
                    <table className="table table-bordered table-sm">
                        <thead style={{ background: '#e9ecef' }}>
                            <tr>
                                <th>IDF No</th>
                                <th>BL No</th>
                                <th>Ref. No</th>
                                <th>File Name (Click to View)</th>
                                <th>Purchase No</th>
                                <th style={{ textAlign: 'right' }}>Amount (TZS)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {chequePayables.map((cp, idx) => {
                                const cpInvoiceCurrencyDifferent = cp.currency === 'TZS' && parseFloat(cp.usd_rate || 0) > 100;
                                const cpHasOldItems = cp.items && cp.items.some(item => !item.foreign_amount || item.foreign_amount === 0) && cp.items.some(item => !item.total_amount);
                                const cpRawTotal = parseFloat(cp.total_amount || 0);
                                const cpDisplayAmount = cpInvoiceCurrencyDifferent && cpHasOldItems && cp.usd_rate
                                    ? Math.round(cpRawTotal * parseFloat(cp.usd_rate) * 100) / 100
                                    : cpRawTotal;
                                return (
                                <tr key={idx}>
                                    <td>{cp.idf_number}</td>
                                    <td>{cp.bl_number}</td>
                                    <td>{cp.cheque_no}</td>
                                    <td>
                                        {cp.invoice_document ? (
                                            <a href="#" onClick={(e) => { e.preventDefault(); openFilePreview(cp.invoice_document, 'Tax Invoice'); }}>
                                                VIEW INVOICE
                                            </a>
                                        ) : '-'}
                                    </td>
                                    <td>{cp.invoice_no}</td>
                                    <td style={{ textAlign: 'right', color: 'red' }}>{formatCurrency(cpDisplayAmount)}</td>
                                </tr>
                                );
                            })}
                        </tbody>
                        <tfoot>
                             <tr>
                                 <td colSpan="5" style={{ textAlign: 'right' }}><strong>TOTAL CHEQUE AMOUNT (TZS)</strong></td>
                                 <td style={{ textAlign: 'right', fontSize: '1.1em', color: 'red', fontWeight: 'bold' }}>
                                     {formatCurrency(chequePayables.reduce((acc, cp) => {
                                         const cpInvoiceCurrencyDifferent = cp.currency === 'TZS' && parseFloat(cp.usd_rate || 0) > 100;
                                         const cpHasOldItems = cp.items && cp.items.some(item => !item.foreign_amount || item.foreign_amount === 0) && cp.items.some(item => !item.total_amount);
                                         const cpRawTotal = parseFloat(cp.total_amount || 0);
                                         const cpDisplayAmount = cpInvoiceCurrencyDifferent && cpHasOldItems && cp.usd_rate
                                             ? Math.round(cpRawTotal * parseFloat(cp.usd_rate) * 100) / 100
                                             : cpRawTotal;
                                         return acc + cpDisplayAmount;
                                     }, 0))}
                                 </td>
                             </tr>
                        </tfoot>
                    </table>
                </div>
             )}

             <div className="form-section">
                <h4>ATTACHED DOCUMENTS {stage2Data.paid_by === 'PAID BOC' && <span className="text-danger" style={{fontSize: '0.8em'}}>(PROOF OF PAYMENT REQUIRED)</span>}</h4>
                <table className="table table-sm" style={{ fontSize: '12px', marginBottom: 0 }}>
                    <thead>
                        <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                            <th style={{ textAlign: 'left', border: 'none', paddingBottom: '4px', width: '1%', whiteSpace: 'nowrap' }}>DOCUMENT TYPE</th>
                            <th style={{ textAlign: 'left', border: 'none', paddingBottom: '4px' }}>VIEW</th>
                            <th style={{ width: '30px', border: 'none' }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {documents.map((doc, idx) => {
                            const normalizedDocType = (doc.type || '').toLowerCase();
                            const isReplaceEligibleType = normalizedDocType === 'tax invoice' || normalizedDocType === 'receipt';
                            const showEfdVerification = isStage1Approval && isTransporter && (doc.existingUrl || doc.file) && normalizedDocType === 'tax invoice';
                            return (
                            <tr key={idx} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                <td style={{ padding: '3px 6px', verticalAlign: 'middle', border: 'none', width: '1%', whiteSpace: 'nowrap' }}>
                                    {doc.id || doc.existingUrl || isExpensesView || isStage3Approval ? (
                                        <strong style={{ fontSize: '12px' }}>{doc.type}</strong>
                                    ) : (
                                        <div style={{ minWidth: 180 }}>
                                            <Select2Dropdown
                                                options={documentTypeOptions}
                                                value={doc.type}
                                                onChange={val => updateDocument(idx, 'type', val)}
                                                valueKey="id"
                                                displayKey="text"
                                                placeholder="SELECT TYPE"
                                                isDisabled={isExpensesView}
                                            />
                                        </div>
                                    )}
                                </td>
                                <td style={{ padding: '3px 6px', verticalAlign: 'middle', border: 'none' }}>
                                    {doc.existingUrl ? (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'nowrap' }}>
                                            <button 
                                                type="button"
                                                onClick={(e) => { e.preventDefault(); openFilePreview(doc.existingUrl, doc.type === 'Other' ? (doc.customName || 'Other') : doc.type); }}
                                                style={{ fontSize: 12, background: '#007bff', color: '#fff', border: 'none', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                                                title="View Document"
                                            >
                                                View
                                            </button>
                                            {showEfdVerification && (
                                                <div className="form-check" style={{ marginBottom: 0, display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        id={`efd-verified-${idx}`}
                                                        checked={doc.efdVerified || false}
                                                        onChange={(e) => updateDocument(idx, 'efdVerified', e.target.checked)}
                                                        style={{ margin: 0 }}
                                                    />
                                                    <label
                                                        className="form-check-label"
                                                        htmlFor={`efd-verified-${idx}`}
                                                        style={{ fontWeight: 'bold', color: '#c0392b', fontSize: '11px', whiteSpace: 'nowrap', marginBottom: 0 }}
                                                    >
                                                        EFD VERIFIED
                                                    </label>
                                                </div>
                                            )}
                                            {isUploadedReceiptReview && isReplaceEligibleType && (
                                                <div className="form-check" style={{ marginBottom: 0, display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        id={`allow-replace-review-${idx}`}
                                                        checked={!!doc.allowReplace}
                                                        onChange={(e) => updateDocument(idx, 'allowReplace', e.target.checked)}
                                                        style={{ margin: 0 }}
                                                    />
                                                    <label
                                                        className="form-check-label"
                                                        htmlFor={`allow-replace-review-${idx}`}
                                                        style={{ fontWeight: 'bold', color: '#c0392b', fontSize: '11px', whiteSpace: 'nowrap', marginBottom: 0 }}
                                                    >
                                                        ALLOW REPLACE
                                                    </label>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        !isExpensesView && !isStage3Approval ? (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'nowrap' }}>
                                                {doc.file && (
                                                    <button
                                                        type="button"
                                                        onClick={(e) => { e.preventDefault(); openFilePreview(URL.createObjectURL(doc.file), doc.type === 'Other' ? (doc.customName || 'Other') : doc.type); }}
                                                        style={{ fontSize: 12, background: '#007bff', color: '#fff', border: 'none', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                                                        title="View Document"
                                                    >
                                                        View
                                                    </button>
                                                )}
                                                <input 
                                                    type="file" 
                                                    className="form-control-file" 
                                                    style={{ fontSize: '12px', maxWidth: '160px' }}
                                                    onChange={e => updateDocument(idx, 'file', e.target.files[0])} 
                                                />
                                            </div>
                                        ) : <span className="text-muted" style={{ fontSize: '12px' }}>No File</span>
                                    )}
                                </td>
                                <td style={{ padding: '3px 4px', verticalAlign: 'middle', border: 'none', textAlign: 'center' }}>
                                    {canManageDocumentRows && !doc.id && !isExpensesView && !isStage3Approval && (
                                        <button type="button" className="btn btn-sm btn-danger" onClick={() => removeDocument(idx)}>&times;</button>
                                    )}
                                </td>
                            </tr>
                            );
                        })}
                        {documents.length === 0 && <tr><td colSpan="3" className="text-center" style={{ border: 'none', color: '#888' }}>No Documents Attached</td></tr>}
                    </tbody>
                    {canManageDocumentRows && !isExpensesView && !isStage3Approval && (
                    <tfoot>
                        <tr>
                            <td colSpan="3" style={{ border: 'none', paddingTop: '4px' }}><button type="button" className="btn btn-sm btn-secondary" onClick={addDocument}>+ ADD DOCUMENT</button></td>
                        </tr>
                    </tfoot>
                    )}
                </table>
            </div>
        </>
        );
    };


    const handleConfirmClientPaid = () => {
        if (isTransporter) {
            const efdRequiredDocs = documents.filter(d =>
                ['tax invoice', 'receipt'].includes((d.type || '').toLowerCase()) && (d.file || d.existingUrl)
            );
            const hasAnyEfdVerified = efdRequiredDocs.some(d => !!d.efdVerified);
            if (efdRequiredDocs.length > 0 && !hasAnyEfdVerified) {
                notifyError('Please check EFD VERIFIED for at least one document (Tax Invoice or Receipt) before confirming Client Paid.');
                return;
            }
            const hasProof = documents.some(d => (d.type || '').toLowerCase() === 'proof of payment' && (d.file || d.existingUrl));
            if (!hasProof) {
                notifyError("Mandatory: Please attach 'Proof of Payment' document first.");
                return;
            }
        }


        const pendingProof = documents.some(d => d.file && !d.id && (d.type || '').toLowerCase() === 'proof of payment');
        if (pendingProof) {
            setShowSaveProceedDialog(true);
            return;
        }

        
        if (onClientPaid) onClientPaid(clientPaidDate);
        setShowClientPaidConfirm(false);
    };

    const handleClientPaidClick = () => {
        setClientPaidDate(new Date().toISOString().split('T')[0]);
        setShowClientPaidConfirm(true);
    };

    const checkDuplication = async () => {
        const invoiceType = formData.invoice_type;
        const invoiceNo = invoiceType === 'PROFORMA' ? formData.proforma_invoice_no : formData.tax_invoice_no;
        const totalAmount = grossTotal || 0;
        const vendor = formData.vendor;

        if (!invoiceNo || invoiceNo.trim() === '') {
            return true; // No invoice number to check
        }

        if (!vendor) {
            return true; // No vendor to check
        }

        try {
            const params = new URLSearchParams();
            if (invoiceType === 'PROFORMA') {
                params.append('proforma_invoice_no', invoiceNo);
            } else if (invoiceType === 'TAX') {
                params.append('tax_invoice_no', invoiceNo);
            }

            const res = await fetch(`${API_PAYABLES}?${params.toString()}`, { headers: getAuthHeaders() });
            if (!res.ok) return true; // If check fails, allow save

            const data = await res.json();
            const existingPayables = data.results || data;

            // Filter out the current payable if editing and only check same vendor
            const otherPayables = existingPayables.filter(p => {
                if (initialData?.id && p.id === initialData.id) return false;
                return String(p.vendor) === String(vendor);
            });

            if (otherPayables.length > 0) {
                const existing = otherPayables[0];
                const existingTotal = existing.total_amount || 0;
                const invoiceTypeName = invoiceType === 'PROFORMA' ? 'Proforma Invoice' : 'Tax Invoice';

                if (Math.abs(parseFloat(existingTotal) - parseFloat(totalAmount)) < 0.01) {
                    // Same invoice number and same total amount
                    return new Promise((resolve) => {
                        showConfirm(
                            'Duplicate Payable Warning',
                            <span>Payable Reference <strong>{existing.reference_no}</strong> exists with same {invoiceTypeName} Number <strong>{invoiceNo}</strong> and same Total Amount <strong>{totalAmount}</strong>. Are you sure you would like to continue saving this payable?</span>,
                            () => resolve(true),
                            () => resolve(false)
                        );
                    });
                } else {
                    // Same invoice number but different total amount
                    return new Promise((resolve) => {
                        showConfirm(
                            'Duplicate Payable Warning',
                            <span>Payable Reference <strong>{existing.reference_no}</strong> exists with same {invoiceTypeName} Number <strong>{invoiceNo}</strong>. Are you sure you would like to continue saving this payable?</span>,
                            () => resolve(true),
                            () => resolve(false)
                        );
                    });
                }
            }

            return true;
        } catch (error) {
            console.error('Error checking duplication:', error);
            return true; // If check fails, allow save
        }
    };

    const handleSubmit = async (e) => {
        if (e && e.preventDefault) e.preventDefault();
        const isClientPaidAction = e?.isClientPaidAction; // Check for custom flag

        // Check for duplication first
        const canProceed = await checkDuplication();
        if (!canProceed) return;

        // Add confirmation dialog for saving
        if (!isClientPaidAction) {
            showConfirm(
                'Confirm Save',
                'Are you sure you have verified all details before saving?',
                () => executeSubmit(e, isClientPaidAction)
            );
            return;
        }

        await executeSubmit(e, isClientPaidAction);
    };

    const executeSubmit = async (e, isClientPaidAction) => {
        if (!formData.job) {
            notifyError("Please select a Job/IDF");
            return;
        }

        // Vendor is a required field when the editable dropdown is shown
        if (!receiptUploadOnly && !initialData?.transporter && !formData.vendor) {
            notifyError("Vendor is required.");
            return;
        }

        if (isTransporter && !receiptUploadOnly && !isClientPaidAction) {
            if (!(formData.invoice_no || '').trim()) {
                notifyError("Tax Invoice Number is required.");
                return;
            }
            if (!formData.invoice_date) {
                notifyError("Tax Invoice Date is required.");
                return;
            }
        }

        // --- Document Validation ---
        if (!receiptUploadOnly) {
            if (isTransporter) {
                const hasTaxInvoice = documents.some(d => (d.type || '').toLowerCase() === 'tax invoice');
                if (!hasTaxInvoice) {
                    notifyError("Mandatory: Please attach a Tax Invoice");
                    return;
                }
            } else {
                const isZnzJob = (jobDetails?.cargo_type || '').toUpperCase() === 'ZNZ';

                if (isZnzJob) {
                    const hasCommercialInvoice = documents.some(d =>
                        (d.type || '').toUpperCase().includes('COMMERCIAL INVOICE')
                    );
                    if (!hasCommercialInvoice) {
                        notifyError("Mandatory: Please attach a Commercial Invoice");
                        return;
                    }
                } else {
                    // 1. Check for BL (Bill of Lading)
                    const hasBL = documents.some(d => {
                        const typeUpper = (d.type || '').toUpperCase();
                        return ['LADING', 'BL', 'AWB', 'RCN', 'AIRWAY BILL', 'ROAD CONSIGNMENT NOTE', 'BILL OF LADING'].some(keyword => typeUpper.includes(keyword));
                    });
                    if (!hasBL) {
                        notifyError("Mandatory: Please attach a Bill of Lading (BL), AWB or RCN");
                        return;
                    }
                }

                // 2. Check for Invoice (Tax Invoice or Proforma Invoice)
                const hasInvoice = documents.some(d => 
                    ['tax invoice', 'proforma invoice'].includes((d.type || '').toLowerCase()) || 
                    ((d.type || '').toLowerCase().includes('invoice') && !((d.type || '').toLowerCase().includes('commercial invoice')))
                );
                if (!hasInvoice) {
                    notifyError("Mandatory: Please attach an Invoice (Proforma or Tax Invoice)");
                    return;
                }
            }
        }

        if (formData.pay_by_control_no) {
             if (!formData.control_no) { notifyError("Control Number is required"); return; }
             if (!formData.control_no_type) { notifyError("Please select GEPG or Other for the Control Number"); return; }
        } else if (formData.control_no && !formData.control_no_type) {
            notifyError("Please select GEPG or Other for the Control Number");
            return;
        }

        // Receipt Upload Mode Validation
        if (receiptUploadOnly) {
            if (!formData.invoice_no || formData.invoice_no.trim() === '') {
                notifyError("Tax Invoice Number is required.");
                return;
            }
            if (!formData.invoice_date) {
                notifyError("Tax Invoice Date is required.");
                return;
            }

            const hasTaxInvoiceDoc = documents.some(d => (d.type || '').toLowerCase() === 'tax invoice' && (d.file || d.existingUrl));
            const hasReceiptDoc = documents.some(d => (d.type || '').toLowerCase() === 'receipt' && (d.file || d.existingUrl));

            if (!hasTaxInvoiceDoc) {
                notifyError("Please upload Tax Invoice document before saving.");
                return;
            }

            /*if (!hasReceiptDoc) {
                notifyError("Please upload Receipt document before saving.");
                return;
            }*/

            if (requiresReceiptDoc && !hasReceiptDoc) {
                notifyError(`Receipt document is required based on vendor configuration`);
                return;
            }

            const hasMissingAllowedReplacement = documents.some((d) => {
                const type = (d.type || '').toLowerCase();
                const isEligible = type === 'tax invoice' || type === 'receipt';
                return isEligible && d.id && d.allowReplace && !d.file;
            });
            if (hasMissingAllowedReplacement) {
                notifyError("Please replace every document marked as Allow Replace before saving.");
                return;
            }
        }
        // ---------------------------

        // EFD Verified validation: at least one Tax Invoice/Receipt doc must be EFD verified (not for transporter portal)
        if (!isTransporter && formData.control_no_type !== 'GEPG') {
            const efdRequiredDocs = documents.filter(d =>
                ['tax invoice', 'receipt'].includes((d.type || '').toLowerCase()) && (d.file || d.existingUrl)
            );
            const hasAnyEfdVerified = efdRequiredDocs.some(d => !!d.efdVerified);
            if (efdRequiredDocs.length > 0 && !hasAnyEfdVerified) {
                notifyError('Please check EFD VERIFIED for at least one document (Tax Invoice or Receipt) before saving.');
                return;
            }
        }

        setSubmitting(true);
        const payload = new FormData();

        payload.append('job', formData.job);
        
        // Stage 2 Approval Handling
        if (isStage2Approval) {
            // Only send Payment Details + Document Updates (if any)
            payload.append('bank_account', stage2Data.bank);
            payload.append('cheque_no', stage2Data.cheque_no);
            payload.append('collected_by', stage2Data.collected_by);
            // Assuming onApprove call handles status update separately, but we might want to consolidate.
            // But here handleSubmit is for "SAVE", while footer has "Approve". 
            // In layout requested, "Approve" button is inside the payment section row.
            // We will hook 'onApprove' to include this data.
        } else if (receiptUploadOnly) {
             // Receipt Upload Only — only save tax invoice number, documents are handled below
             payload.append('invoice_type', 'TAX');
             payload.append('invoice_no', formData.invoice_no);
             payload.append('tax_invoice_no', formData.invoice_no || '');
        } else {
             // Standard Edit/Create Payload
             payload.append('invoice_type', formData.invoice_type);
             payload.append('invoice_no', formData.invoice_no);
             payload.append('proforma_invoice_no', formData.proforma_invoice_no || '');
             payload.append('tax_invoice_no', formData.tax_invoice_no || '');
             payload.append('invoice_date', formData.invoice_date);
             
             // If Invoice Currency is Different, save effective rate as usd_rate for consistency
             if (invoiceCurrencyDifferent && invoiceCurrencyData.fx_rate) {
                 const rawRate = parseFloat(invoiceCurrencyData.fx_rate);
                 const rateToSave = fxRoundOff ? Math.round(rawRate * 100) / 100 : rawRate;
                 payload.append('usd_rate', rateToSave);
                 payload.append('total_fx', invoiceCurrencyData.total_fx || '');
                 payload.append('total_tzs', invoiceCurrencyData.total_tzs || '');
             } else {
                 payload.append('usd_rate', formData.usd_rate);
                 payload.append('total_fx', '');
                 payload.append('total_tzs', '');
             }
             payload.append('fx_round_off', fxRoundOff);
             
             payload.append('currency', formData.currency);
             if(formData.vendor) payload.append('vendor', formData.vendor);
             if(formData.transporter) payload.append('transporter', formData.transporter);
             payload.append('remarks', formData.remarks);
             payload.append('pay_by_control_no', formData.pay_by_control_no);
             payload.append('control_no', formData.control_no);
             payload.append('control_no_type', formData.control_no_type);

             // Include module for reference number generation on create
             if (!initialData || !initialData.id) {
                 payload.append('module', module || 'PERMIT');
             }

             if (initialData && initialData.status === 'REJECTED') {
                 payload.append('status', 'PENDING');
                 payload.append('rejection_reason', '');
             }

             // ALWAYS send items now, regardless of GEPG or Other
             // Add vat_amount and total_amount to each item for proper saving
             // For transporter payables, preserve the manually entered total instead of recalculating from net + VAT
             const itemsWithTotals = isTransporter ? items : items.map(item => ({
                 ...item,
                 vat_amount: item.add_vat ? (parseFloat(item.amount) * 0.18).toFixed(2) : '0.00',
                 total_amount: item.add_vat ? (parseFloat(item.amount) * 1.18).toFixed(2) : item.amount
             }));
             payload.append('items', JSON.stringify(itemsWithTotals));
             
             // Send totals for proper saving
             payload.append('net_amount', netTotal);
             payload.append('vat_amount', vatTotal);
             payload.append('gross_amount', grossTotal);
        }

        const newDocs = documents.filter(d => d.type && d.file && !d.id);
        const docsMeta = newDocs.map(d => ({ type: d.type === 'Other' ? (d.customName || 'Other') : d.type }));
        
        if (docsMeta.length > 0) {
            payload.append('documents_metadata', JSON.stringify(docsMeta));
            newDocs.forEach((doc) => {
                payload.append('document_files', doc.file); 
            });
        }

        const replaceDocs = documents.filter(d => d.id && d.file && d.allowReplace);
        const replaceDocsMeta = replaceDocs.map(d => ({
            id: d.id,
            type: d.type === 'Other' ? (d.customName || 'Other') : d.type
        }));

        const isReceiptReplacementResubmission = !!(
            initialData &&
            replaceDocsMeta.length > 0 &&
            (
                initialData.allow_replace_tax_invoice ||
                initialData.allow_replace_receipt ||
                initialData.receipt_rejection_reason
            )
        );

        if (receiptUploadOnly || isReceiptReplacementResubmission) {
            payload.append('receipt_uploaded', 'true');
            payload.append('receipt_approved', 'false');
            payload.append('status', 'APPROVED 3');
            payload.append('allow_replace_tax_invoice', 'false');
            payload.append('allow_replace_receipt', 'false');
            payload.append('receipt_rejection_reason', '');
        }

        if (replaceDocsMeta.length > 0) {
            payload.append('replace_documents_metadata', JSON.stringify(replaceDocsMeta));
            replaceDocs.forEach((doc) => {
                payload.append('replace_document_files', doc.file);
            });
        }

        if (docIdsToDelete.length > 0) {
            payload.append('document_ids_to_delete', JSON.stringify(docIdsToDelete));
        }

        const isEdit = !!(initialData?.id);
        const url = isEdit ? `${API_PAYABLES}${initialData.id}/` : API_PAYABLES;
        const method = isEdit ? (receiptUploadOnly ? 'PATCH' : 'PUT') : 'POST';

        try {
            const res = await fetch(url, {
                method,
                headers: getAuthHeaders(true),
                body: payload
            });

            if (res.ok) {
                notifySuccess(`Payable ${isEdit ? 'Updated' : 'Created'}`);
                if (onSave) onSave();
                
                if (isClientPaidAction && onClientPaid) {
                    onClientPaid(clientPaidDate);
                } else {
                    onClose();
                }
            } else {
                const err = await res.json();
                notifyError("Failed: " + JSON.stringify(err));
            }
        } catch (error) {
            notifyError("Error: " + error.message);
        } finally {
            setSubmitting(false);
        }
    };

    // Modified Approve Handler for Stage 2
    const handleStage2Approve = () => {
         showConfirm(
             'Approve Payable',
             'Are you sure you want to approve this payable?',
             () => executeStage2Approve()
         );
    };

    const executeStage2Approve = async () => {
         if (!stage2Data.bank) { notifyError("Please select a Bank or Cash"); return; }
         if (!stage2Data.cheque_no && stage2Data.bank !== 'CASH') { notifyError("Please enter Cheque/Ref No"); return; }
         if (!stage2Data.collected_by) { notifyError("Please enter Collected By"); return; }
         if (!stage2Data.paid_by) { notifyError("Please select Paid By"); return; }

         if (stage2Data.paid_by === 'PAID BOC') {
             const hasProof = documents.some(d => (d.type || '').toLowerCase() === 'proof of payment');
             if (!hasProof) {
                 notifyError("Mandatory: Please attach 'Proof of Payment' for 'PAID BOC'.");
                 return;
             }
         }

         setSubmitting(true);
         const payload = new FormData();
         payload.append('bank_account', stage2Data.bank);
         payload.append('cheque_no', stage2Data.cheque_no);
         payload.append('collected_by', stage2Data.collected_by);
         payload.append('paid_by', stage2Data.paid_by);
         
         // IMPORTANT: Also update status to move to next stage
         if (stage2Data.bank === 'CASH') {
             // Cash payments skip Stage 3 (Approved 2) and go directly to Expenses (Approved 3)
             payload.append('status', 'APPROVED 3');
             // Auto-fill Stage 3 requirements
             payload.append('payment_date', new Date().toISOString().split('T')[0]);
             payload.append('tiss_tips', 'NO TISS/TIPS');
         } else {
             payload.append('status', 'APPROVED 2');
         }

         // Handle New Documents in Stage 2 (Proof of Payment etc)
         const newDocs = documents.filter(d => d.type && d.file && !d.id);
         const docsMeta = newDocs.map(d => ({ type: d.type === 'Other' ? (d.customName || 'Other') : d.type }));
         
         if (docsMeta.length > 0) {
             payload.append('documents_metadata', JSON.stringify(docsMeta));
             newDocs.forEach((doc) => {
                 payload.append('document_files', doc.file); 
             });
         }

         try {
            const res = await fetch(`${API_PAYABLES}${initialData.id}/`, {
                method: 'PATCH',
                headers: getAuthHeaders(true),
                body: payload
            });
            if(res.ok) {
                notifySuccess("Approved & Moved to Stage 3");
                if (onSave) onSave(); // Refresh list
                onClose();
            } else {
                const err = await res.json();
                notifyError("Failed: " + JSON.stringify(err));
            }
         } catch(e) {
             notifyError(e.message);
         } finally {
             setSubmitting(false);
         }
    };

    const handleStage3Approve = () => {
         showConfirm(
             'Confirm Approval',
             'Are you sure you want to approve this payable?',
             () => executeStage3Approve()
         );
    };

    const executeStage3Approve = async () => {
         if (!stage3Data.payment_date) { notifyError("Please select a Payment Date"); return; }
         if (!isGepgPayable && !isCash && !stage3Data.tiss_tips) {
             setShowStage3TissError(true);
             notifyError("Please select TISS/TIPS option");
             return;
         }
         setShowStage3TissError(false);

         setSubmitting(true);
         const payload = new FormData();
         payload.append('payment_date', stage3Data.payment_date);
         payload.append('tiss_tips', stage3Data.tiss_tips);
         if (stage3Data.tiss_tips === 'TISS' || stage3Data.tiss_tips === 'TIPS') {
             payload.append('tiss_tips_amount', stage3TissTipsAmount.toFixed(2));
         } else {
             payload.append('tiss_tips_amount', '0');
         }
         if (isGepgPayable) {
             payload.append('gepg_amount', gepgAmount.toFixed(2));
         }

         // If Control No Payment, include the Bank selection (Payment Source)
         if (formData.pay_by_control_no) {
             // We use stage3Data.bank if set, else fallback to stage2Data (unlikely to be set in S2 for control no)
             // But let's check:
             if (!stage3Data.payment_source) {
                  notifyError("Please select Payment Source (Bank/Cash)");
                  setSubmitting(false);
                  return;
             }
             payload.append('bank_account', stage3Data.payment_source);
         }
         
         // Move to next stage (Expenses / APPROVED 3)
         payload.append('status', 'APPROVED 3');

         // Handle New Documents in Stage 3 (Proof of Payment etc)
         const newDocs = documents.filter(d => d.type && d.file && !d.id);
         const docsMeta = newDocs.map(d => ({ type: d.type === 'Other' ? (d.customName || 'Other') : d.type }));
         
         if (docsMeta.length > 0) {
             payload.append('documents_metadata', JSON.stringify(docsMeta));
             newDocs.forEach((doc) => {
                 payload.append('document_files', doc.file); 
             });
         }

         try {
            const res = await fetch(`${API_PAYABLES}${initialData.id}/`, {
                method: 'PATCH',
                headers: getAuthHeaders(true),
                body: payload
            });
            if(res.ok) {
                notifySuccess("Approved & Moved to Expenses");
                if (onSave) onSave();
                onClose();
            } else {
                const err = await res.json();
                notifyError("Failed: " + JSON.stringify(err));
            }
         } catch(e) {
             notifyError(e.message);
         } finally {
             setSubmitting(false);
         }
    };

    return (
        <div
            className="dialog-container shipping-line-payables-compact"
            style={{
                width: '95%',
                maxWidth: popoutMode ? 'none' : '1300px',
                height: popoutMode ? '95vh' : 'auto',
                maxHeight: popoutMode ? '95vh' : '75vh',
                display: 'flex',
                flexDirection: 'column',
                margin: popoutMode ? '2vh auto' : '0 auto',
                fontSize: '10px',
                fontFamily: 'Arial, sans-serif'
            }}
        >
            <style>
                {`
                    .shipping-line-payables-compact .dialog-header { padding: 6px 10px; }
                    .shipping-line-payables-compact .dialog-header h3 { margin: 0; font-size: 13px; font-family: Arial, sans-serif; font-weight: 600; }
                    .shipping-line-payables-compact .dialog-body { padding: 8px !important; }
                    .shipping-line-payables-compact .form-section { margin-bottom: 6px; }
                    .shipping-line-payables-compact .form-section h4 { margin: 0 0 5px 0; padding-bottom: 3px; font-size: 11px; font-family: Arial, sans-serif; }
                    .shipping-line-payables-compact label,
                    .shipping-line-payables-compact small,
                    .shipping-line-payables-compact .form-check-label,
                    .shipping-line-payables-compact .text-muted { font-size: 10px; font-family: Arial, sans-serif; color: #555; margin: 2px 0 1px !important; display: block; }
                    .shipping-line-payables-compact .form-control,
                    .shipping-line-payables-compact input:not([type="checkbox"]):not([type="radio"]),
                    .shipping-line-payables-compact select { height: 22px !important; font-size: 10px; font-family: Arial, sans-serif; padding: 1px 5px; }
                    .shipping-line-payables-compact textarea.form-control { height: auto !important; min-height: 36px; font-size: 10px; font-family: Arial, sans-serif; padding: 3px 5px; }
                    .shipping-line-payables-compact .select2-selection { min-height: 22px !important; padding: 1px 5px !important; font-size: 10px !important; }
                    .shipping-line-payables-compact .select2-selection__rendered { font-size: 10px !important; }
                    .shipping-line-payables-compact .select2-search { padding: 3px !important; }
                    .shipping-line-payables-compact .select2-search__field { font-size: 10px !important; padding: 2px 4px !important; }
                    .shipping-line-payables-compact .select2-results__option { font-size: 10px !important; padding: 4px 6px !important; }
                    .shipping-line-payables-compact table th,
                    .shipping-line-payables-compact table td { padding: 2px 4px !important; font-size: 10px; font-family: Arial, sans-serif; vertical-align: middle; }
                    .shipping-line-payables-compact .btn,
                    .shipping-line-payables-compact .dialog-btn { padding: 3px 7px; font-size: 10px; font-family: Arial, sans-serif; }
                    .shipping-line-payables-compact .docs-table th:first-child { width: 32% !important; }
                    .shipping-line-payables-compact .docs-table th:nth-child(3) { width: 42px !important; }
                `}
            </style>
            <div className="dialog-header dis-flex-between">
                <div>
                   <h3>{mode === 'edit' ? 'EDIT PAYABLE' : mode === 'view' ? 'VIEW PAYABLE' : 'NEW PAYABLE'} 
                   {isStage2Approval ? ' - STAGE 2 APPROVAL' : isStage3Approval ? ' - STAGE 3 APPROVAL' : ''}
                   {mode === 'view' && initialData?.reference_no ? ` - ${initialData.reference_no}` : ''}</h3>
                   {mode !== 'view' && initialData?.reference_no && (
                       <span style={{ fontSize: '1em', color: '#ffffff', fontWeight: '600', letterSpacing: '0.05em' }}>
                           Ref: {initialData.reference_no}
                       </span>
                   )}
                </div>
                {showViewExpenses && (initialData?.idf_number || initialData?.job_details?.idf_no || jobDetails?.idf_no) && (
                    <div style={{ marginLeft: 'auto', marginRight: 10 }}>
                        <button
                            type="button"
                            className="btn btn-sm btn-warning"
                            style={{ color: 'orange' }}
                            onClick={() => setShowExpensesModal(true)}
                        >
                            <i className="fa fa-money"></i> View Expenses
                        </button>
                    </div>
                )}
                {tasacLink && (
                    <div>
                        <button
                            type="button"
                            className="btn btn-sm btn-info"
                            onClick={() => openFilePreview(tasacLink, 'Tasac Fees')}
                        >
                            <i className="fa fa-file-pdf-o"></i> Tasac Fees PDF
                        </button>
                    </div>
                )}
            </div>
            
            <div className="dialog-body" style={{ flex: 1, overflowY: 'auto', padding: '12px' }}>
                <form onSubmit={handleSubmit}>
                    {((approvalMode || isExpensesView || mode === 'view') && !receiptUploadOnly) ? renderApprovalContent() : (
                        <>
                        {/* JOB INFO */}
                        <div className="form-section">
                        <h4>JOB SELECTION</h4>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 8 }}>
                            <div style={{ position: 'relative' }}>
                                <label className="required">SEARCH JOB</label>
                                {(mode === 'edit' || mode === 'view') ? (
                                    <div style={{ display: 'grid', gridTemplateColumns: '210px minmax(240px, 420px)', gap: '8px', alignItems: 'center' }}>
                                        <div className="form-control" style={{ backgroundColor: '#e9ecef', fontWeight: 'bold' }}>
                                            {JOB_SEARCH_FIELDS.find(f => f.id === getDefaultReadOnlySearchInfo().field)?.text || 'IDF No.'}
                                        </div>
                                        <div className="form-control" style={{ backgroundColor: '#e9ecef', fontWeight: 'bold' }}>
                                            {getDefaultReadOnlySearchInfo().value || '-'}
                                        </div>
                                    </div>
                                ) : isTransporter ? (
                                    <div style={{ display: 'grid', gridTemplateColumns: '210px minmax(240px, 420px)', gap: '8px', alignItems: 'center' }}>
                                        <div className="form-control" style={{ backgroundColor: '#e9ecef', fontWeight: 'bold' }}>IDF NO</div>
                                        <div className="form-control" style={{ backgroundColor: '#e9ecef', fontWeight: 'bold' }}>
                                            {jobDetails?.idf_no || initialData?.idf_number || initialData?.job_details?.idf_no || '-'}
                                        </div>
                                    </div>
                                ) : (
                                    <div style={{ display: 'grid', gridTemplateColumns: '210px minmax(240px, 420px)', gap: '8px', alignItems: 'center' }}>
                                        <div>
                                            <Select2Dropdown
                                                options={JOB_SEARCH_FIELDS}
                                                value={jobSearchField}
                                                onChange={(val) => {
                                                    setJobSearchField(val);
                                                    setJobSearchTerm('');
                                                    setIdfOptions([]);
                                                    setJobDetails(null);
                                                }}
                                                valueKey="id"
                                                displayKey="text"
                                                placeholder="SELECT SEARCH FIELD"
                                                required
                                                isDisabled={!!jobDetails}
                                            />
                                        </div>
                                        <input 
                                            type="text" 
                                            value={jobSearchTerm}
                                            onChange={(e) => {
                                                setJobSearchTerm(e.target.value);
                                                setActiveDropdownIndex(-1);
                                                if(!e.target.value) setJobDetails(null);
                                            }}
                                            onKeyDown={(e) => {
                                                if (idfOptions.length === 0) return;
                                                if (e.key === 'ArrowDown') {
                                                    e.preventDefault();
                                                    setActiveDropdownIndex(prev => Math.min(prev + 1, idfOptions.length - 1));
                                                } else if (e.key === 'ArrowUp') {
                                                    e.preventDefault();
                                                    setActiveDropdownIndex(prev => Math.max(prev - 1, 0));
                                                } else if (e.key === 'Enter') {
                                                    if (activeDropdownIndex >= 0 && activeDropdownIndex < idfOptions.length) {
                                                        e.preventDefault();
                                                        handleSelectJob(idfOptions[activeDropdownIndex]);
                                                        setActiveDropdownIndex(-1);
                                                    }
                                                } else if (e.key === 'Escape') {
                                                    setIdfOptions([]);
                                                    setActiveDropdownIndex(-1);
                                                }
                                            }}
                                            placeholder={jobSearchField ? `TYPE TO SEARCH ${JOB_SEARCH_FIELDS.find(f => f.id === jobSearchField)?.text?.toUpperCase() || 'VALUE'}...` : 'SELECT SEARCH FIELD FIRST...'}
                                            disabled={!jobSearchField || !!jobDetails}
                                            required
                                            className="form-control"
                                        />
                                    </div>
                                )}
                                {idfOptions.length > 0 && !jobDetails && (
                                    <div className="dropdown-results" style={{
                                        position: 'absolute', left: '218px', width: 'min(420px, calc(100% - 218px))', zIndex: 1000,
                                        background: 'white', border: '1px solid #ccc', maxHeight: '200px', overflowY: 'auto',
                                        boxShadow: '0 2px 6px rgba(0,0,0,0.15)'
                                    }}>
                                        {idfOptions.map((opt, idx) => (
                                            <div key={opt.id}
                                                onClick={() => { handleSelectJob(opt); setActiveDropdownIndex(-1); }}
                                                onMouseEnter={() => setActiveDropdownIndex(idx)}
                                                onMouseLeave={() => setActiveDropdownIndex(-1)}
                                                style={{
                                                    padding: '6px 10px',
                                                    cursor: 'pointer',
                                                    backgroundColor: idx === activeDropdownIndex ? '#0d6efd' : 'white',
                                                    color: idx === activeDropdownIndex ? '#fff' : '#212529',
                                                    transition: 'background-color 0.1s'
                                                }}
                                            >
                                                <strong>{getJobSearchValue(opt, jobSearchField || 'idf_no')}</strong> - {opt.customer}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                        {jobDetails && (
                            <div className="form-section" style={{ marginTop: '8px', backgroundColor: '#e8f5e9', padding: '8px 12px', borderRadius: '5px', border: '1px solid #c8e6c9' }}>
                            <h4 style={{ color: '#2e7d32', borderBottom: '1px solid #a5d6a7', paddingBottom: '5px' }}>JOB DETAILS</h4>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px' }}>
                                <div>
                                    <small style={{ color: '#666' }}>JOB ID</small>
                                    <div style={{ fontWeight: 'bold' }}>{jobDetails.id}</div>
                                </div>
                                <div>
                                    <small style={{ color: '#666' }}>FILE NO</small>
                                    <div style={{ fontWeight: 'bold' }}>{jobDetails.kk_file_no}</div>
                                </div>
                                <div>
                                    <small style={{ color: '#666' }}>IDF NO</small>
                                    <div style={{ fontWeight: 'bold' }}>{jobDetails.idf_no}</div>
                                </div>
                                <div>
                                    <small style={{ color: '#666' }}>CUSTOMER</small>
                                    <div style={{ fontWeight: 'bold' }}>{jobDetails.customer}</div>
                                </div>
                                <div>
                                    {jobDetails.customer_tin && (<>
                                        <small style={{ color: '#666' }}>CUSTOMER TIN</small>
                                        <div style={{ fontWeight: 'bold' }}>{jobDetails.customer_tin}</div>
                                    </>)}
                                </div>                                
                                <div>
                                    <small style={{ color: '#666' }}>BL NUMBER</small>
                                    <div style={{ fontWeight: 'bold' }}>{jobDetails.bl_no}</div>
                                </div>
                                <div style={{ gridColumn: 'span 3' }}>
                                    <small style={{ color: '#666' }}>CONTAINERS</small>
                                    <div style={{ fontWeight: 'bold' }}>
                                        {jobDetails.container_summary}
                                        {isTransporter && (jobDetails.containers_list || []).length > 0 && (
                                            <span style={{ color: '#c0392b', marginLeft: 10 }}>
                                                | {(jobDetails.containers_list || []).map(c => c.container_no || c.number || '').filter(Boolean).join(' | ')}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        )}
                        </div>

                    {/* INVOICE DETAILS */}
                    <div className="form-section" style={{ marginTop: '20px' }}>
                        <h4>INVOICE DETAILS</h4>
                        <div style={{ display: 'grid', gridTemplateColumns: receiptUploadOnly ? 'repeat(6, 1fr)' : 'repeat(5, 1fr)', gap: 8 }}>
                             <div style={{gridColumn: 'span 2'}}>
                                <label className="required">VENDOR</label>
                                {receiptUploadOnly ? (
                                    <div className="form-control" style={{ backgroundColor: '#e9ecef' }}>
                                         {initialData?.vendor_name || initialData?.transporter_name ? (
                                             (initialData.vendor_name || '').toString()
                                         ) : (
                                             vendorOptions.find(v => v.id === formData.vendor)?.text || formData.vendor || '-'
                                         )}
                                    </div>
                                ) : initialData?.transporter ? (
                                    <div className="form-control" style={{ backgroundColor: '#e9ecef', fontWeight: 'bold' }}>
                                        {initialData.vendor_name || initialData.transporter_name || ''}
                                    </div>
                                ) : (
                                <Select2Dropdown
                                    options={vendorOptions}
                                    value={formData.vendor}
                                    onChange={val => setFormData({ ...formData, vendor: val })}
                                    valueKey="id"
                                    displayKey="text"
                                    isDisabled={isFieldDisabled}
                                    required
                                    placeholder="SELECT VENDOR"
                                />
                                )}
                            </div>
                             <div style={{gridColumn: receiptUploadOnly ? 'span 4' : 'span 3'}}></div>

                            <div>
                                <label className="required">TYPE</label>
                                {receiptUploadOnly ? (
                                    <div className="form-control" style={{ backgroundColor: '#e9ecef', fontWeight: 'bold' }}>Proforma</div>
                                ) : (
                                    <Select2Dropdown
                                        options={invoiceOptions}
                                        value={formData.invoice_type}
                                        onChange={handleInvoiceTypeChange}
                                        valueKey="value"
                                        displayKey="label"
                                        isDisabled={isInvoiceEditDisabled}
                                        required
                                    />
                                )}
                            </div>
                            <div>
                                <label className="required">{isTransporter ? 'TAX INVOICE NO' : 'PROFORMA NO'}</label>
                                {receiptUploadOnly ? (
                                    <div className="form-control" style={{ backgroundColor: '#e9ecef', fontWeight: 'bold' }}>{proformaNoDisplay}</div>
                                ) : (
                                    <input 
                                        className="form-control"
                                        value={formData.invoice_no} 
                                        onChange={e => handleInvoiceNoChange(e.target.value)}
                                        disabled={isInvoiceEditDisabled} 
                                        required
                                        placeholder={`ENTER ${formData.invoice_type} NO`}
                                    />
                                )}
                            </div>
                            <div>
                                <label className="required">INVOICE DATE</label>
                                {receiptUploadOnly ? (
                                    <div className="form-control" style={{ backgroundColor: '#e9ecef', fontWeight: 'bold' }}>{formatDateDisplay(formData.invoice_date)}</div>
                                ) : (
                                    <input 
                                        type="date" 
                                        max={new Date().toISOString().split('T')[0]}
                                        className="form-control"
                                        value={formData.invoice_date} 
                                        onChange={e => setFormData({...formData, invoice_date: e.target.value})} 
                                        disabled={isDateDisabled} 
                                        required
                                    />
                                )}
                            </div>
                            {receiptUploadOnly && (
                                <>
                                    <div>
                                        <label className="required">TAX INVOICE TYPE</label>
                                        <div className="form-control" style={{ backgroundColor: '#e9ecef', fontWeight: 'bold' }}>Tax Invoice</div>
                                    </div>
                                    <div>
                                        <label className="required">TAX INVOICE NO</label>
                                        <input
                                            className="form-control"
                                            value={formData.invoice_no}
                                            onChange={e => handleInvoiceNoChange(e.target.value.toUpperCase())}
                                            disabled={isView}
                                            required
                                            placeholder="ENTER TAX INVOICE NO"
                                            style={{ textTransform: 'uppercase' }}
                                        />
                                    </div>
                                    <div>
                                        <label className="required">TAX INVOICE DATE</label>
                                        <input
                                            type="date"
                                            max={new Date().toISOString().split('T')[0]}
                                            className="form-control"
                                            value={formData.invoice_date}
                                            onChange={e => setFormData({ ...formData, invoice_date: e.target.value })}
                                            disabled={isView}
                                            required
                                        />
                                    </div>
                                </>
                            )}
                            <div>
                                <label className="required">CURRENCY</label>
                                {receiptUploadOnly || isTransporter ? (
                                    <div className="form-control" style={{ backgroundColor: '#e9ecef', fontWeight: 'bold' }}>{formData.currency}</div>
                                ) : (
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <div style={{ flex: 1 }}>
                                        <Select2Dropdown
                                            options={CURRENCIES}
                                            value={formData.currency}
                                            onChange={val => setFormData({ ...formData, currency: val, usd_rate: val === 'TZS' ? '1.00' : formData.usd_rate })}
                                            valueKey="value"
                                            displayKey="label"
                                            isDisabled={isFieldDisabled}
                                            required
                                        />
                                    </div>
                                    {!isFieldDisabled && (
                                        <div style={{ marginLeft: '10px', display: 'flex', alignItems: 'center' }}>
                                            <input 
                                                type="checkbox" 
                                                id="currDiff"
                                                checked={invoiceCurrencyDifferent}
                                                onChange={e => setInvoiceCurrencyDifferent(e.target.checked)}
                                                style={{ transform: 'scale(1.2)', marginRight: '5px' }}
                                            />
                                            <label htmlFor="currDiff" style={{ fontSize: '0.8em', marginBottom: 0, cursor: 'pointer' }}>DIFF</label>
                                        </div>
                                    )}
                                </div>
                                )}
                            </div>
                            {formData.currency !== 'TZS' && !invoiceCurrencyDifferent && (<>
                                <div>
                                    <label className="required">EXCH RATE</label>
                                    {receiptUploadOnly ? (
                                        <div className="form-control" style={{ backgroundColor: '#e9ecef', fontWeight: 'bold' }}>{formData.usd_rate}</div>
                                    ) : (
                                    <FormattedNumberInput 
                                        value={formData.usd_rate} 
                                        onChange={val => setFormData({...formData, usd_rate: val})} 
                                        disabled={isFieldDisabled} 
                                        required
                                    />
                                    )}
                                </div>
                                </>
                            )}
                        </div>
                    </div>

                    {/* DIFFERENT CURRENCY TOOL */}

                    {invoiceCurrencyDifferent && (
                        <div className="form-section" style={{ marginTop: '20px', background: '#ffeeba', border: '1px solid #ffdf7e', padding: '15px' }}>
                            <h4 style={{ color: '#856404', fontSize: '1em' }}>INVOICE CURRENCY CONVERSION</h4>
                            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr 1fr 1fr 120px', gap: '8px', alignItems: 'end' }}>
                                <div>
                                    <label>INVOICE CURRENCY</label>
                                    <div className="form-control" style={{ backgroundColor: '#e9ecef', fontWeight: 'bold', minWidth: 0 }}>
                                        {formData.currency === 'USD' ? 'TZS' : 'USD'}
                                    </div>
                                </div>
                                <div>
                                    <label>TOTAL {invoiceCurrencyData.currency} AMOUNT</label>
                                    <FormattedNumberInput
                                        value={invoiceCurrencyData.total_fx}
                                        onChange={val => setInvoiceCurrencyData({ ...invoiceCurrencyData, total_fx: val })}
                                        disabled={isFieldDisabled}
                                        style={{ minWidth: 0 }}
                                    />
                                </div>
                                <div>
                                    <label>TOTAL TZS AMOUNT</label>
                                    <FormattedNumberInput
                                        value={invoiceCurrencyData.total_tzs}
                                        onChange={val => setInvoiceCurrencyData({ ...invoiceCurrencyData, total_tzs: val })}
                                        disabled={isFieldDisabled}
                                        style={{ minWidth: 0 }}
                                    />
                                </div>
                                <div>
                                    <label>CALCULATED FX RATE</label>
                                    <div style={{ fontWeight: 'bold', fontSize: '1.2em', padding: '5px', background: 'white', border: '1px solid #ced4da', borderRadius: '4px', minWidth: 0 }}>
                                        {invoiceCurrencyData.fx_rate
                                            ? formatFxRate(
                                                fxRoundOff
                                                    ? Number(parseFloat(invoiceCurrencyData.fx_rate).toFixed(2))
                                                    : Number(parseFloat(invoiceCurrencyData.fx_rate).toFixed(4)),
                                                fxRoundOff ? 2 : 4
                                            )
                                            : '-'
                                        }
                                    </div>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'start', justifyContent: 'end', minWidth: 0 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <input
                                            type="checkbox"
                                            id="fxRoundOff"
                                            checked={fxRoundOff}
                                            onChange={e => setFxRoundOff(e.target.checked)}
                                            disabled={isFieldDisabled}
                                            style={{ transform: 'scale(1.2)', marginRight: '4px' }}
                                        />
                                        <label htmlFor="fxRoundOff" style={{ fontWeight: 'bold', marginBottom: 0, cursor: 'pointer', color: '#856404' }}>ROUND OFF</label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {/* REMARKS & CONTROL NO */}
                    <div className="form-section" style={{ marginTop: '20px' }}>
                         <div style={{ marginBottom: '15px' }}>
                            <label>REMARKS</label>
                            <textarea
                                className="form-control"
                                rows="2"
                                value={formData.remarks}
                                onChange={e => setFormData({...formData, remarks: e.target.value})}
                                disabled={isFieldDisabled}
                            />
                        </div>

                        {!isTransporter && (
                        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginBottom: '15px' }}>
                             <input 
                                type="checkbox"
                                id="payByControlNo"
                                checked={formData.pay_by_control_no}
                                onChange={e => setFormData({...formData, pay_by_control_no: e.target.checked})}
                                disabled={isFieldDisabled}
                                style={{ transform: 'scale(1.2)', marginRight: '10px' }}
                             />
                             <label style={{ fontWeight: 'bold', marginBottom: 0, cursor: 'pointer' }} htmlFor="payByControlNo">PAY BY CONTROL NO.</label>
                        </div>
                        )}

                        {formData.pay_by_control_no && (
                            <div style={{ padding: '15px', background: '#e9ecef', borderRadius: '4px', marginBottom: '15px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: '1 1 300px' }}>
                                        <label style={{ whiteSpace: 'nowrap', marginBottom: 0 }}>CONTROL NUMBER:</label>
                                        <input 
                                            className="form-control"
                                            value={formData.control_no}
                                            onChange={e => setFormData({...formData, control_no: e.target.value})}
                                            disabled={isView}
                                            style={{ minWidth: '200px' }}
                                        />
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <label style={{ marginBottom: 0, fontWeight: 'bold' }}>TYPE:</label>
                                        <div className="form-check" style={{ marginBottom: 0 }}>
                                            <input 
                                                className="form-check-input" type="radio" name="controlType" id="typeGepg"
                                                checked={formData.control_no_type === 'GEPG'}
                                                onChange={() => setFormData({...formData, control_no_type: 'GEPG'})}
                                                disabled={isView}
                                            />
                                            <label className="form-check-label" htmlFor="typeGepg" style={{ marginLeft: '5px' }}>GEPG</label>
                                        </div>
                                        <div className="form-check" style={{ marginBottom: 0 }}>
                                            <input 
                                                className="form-check-input" type="radio" name="controlType" id="typeOther"
                                                checked={formData.control_no_type === 'OTHER'}
                                                onChange={() => setFormData({...formData, control_no_type: 'OTHER'})}
                                                disabled={isView}
                                            />
                                            <label className="form-check-label" htmlFor="typeOther" style={{ marginLeft: '5px' }}>OTHER</label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* ITEMS */}
                    <div className="form-section">
                        <h4>PAYABLE ITEMS</h4>
                        <table className="table table-bordered table-sm docs-table">
                        <thead>
                            <tr>
                                <th>CHARGE NAME</th>
                                {!isTransporter && <th style={{width: '100px'}}>UNITS</th>}
                                {!isTransporter && <th style={{width: '180px'}}>UNIT TYPE</th>}
                                {!isTransporter && <th style={{width: '140px'}}>UNIT RATE {invoiceCurrencyDifferent ? `(${invoiceCurrencyData.currency})` : ''}</th>}
                                {invoiceCurrencyDifferent && <th style={{width: '180px'}}>{invoiceCurrencyData.currency} AMOUNT</th>}
                                {invoiceCurrencyDifferent && <th style={{width: '150px'}}>{invoiceCurrencyData.currency} VAT</th>}
                                {invoiceCurrencyDifferent && <th style={{width: '180px'}}>{invoiceCurrencyData.currency} TOTAL</th>}
                                <th style={{width: '180px'}}>AMOUNT{formData.currency === 'TZS' ? ' (TZS)' : ''}</th>
                                <th style={{width: '80px', textAlign: 'center'}}>VAT</th>
                                <th style={{width: '180px'}}>VAT AMOUNT</th>
                                <th style={{width: '180px'}}>TOTAL AMOUNT</th>
                                {!isFieldDisabled && <th style={{width: '40px'}}></th>}
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((item, idx) => (
                                <tr key={idx}>
                                    
                                        <>
                                            <td>
                                                {receiptUploadOnly ? (
                                                    <div style={{ padding: '8px' }}>{chargeOptions.find(c => (c.id || '').toUpperCase() === (item.charge_name || '').toUpperCase())?.text || item.charge_name}</div>
                                                ) : (
                                                <Select2Dropdown
                                                    options={chargeOptions}
                                                    value={chargeOptions.find(c => (c.id || '').toUpperCase() === (item.charge_name || '').toUpperCase())?.id || item.charge_name}
                                                    onChange={val => updateItem(idx, 'charge_name', val)}
                                                    valueKey="id"
                                                    displayKey="text"
                                                    isDisabled={isFieldDisabled}
                                                    placeholder="CHARGE"
                                                />
                                                )}
                                            </td>
                                            {!isTransporter && (
                                            <td>
                                                {receiptUploadOnly ? (
                                                    <div style={{ padding: '8px', textAlign: 'right' }}>{formatCurrency(item.units)}</div>
                                                ) : (
                                                <FormattedNumberInput
                                                    value={item.units}
                                                    onChange={val => updateItem(idx, 'units', val)}
                                                    disabled={isFieldDisabled || item.charge_name === 'ADJUSTMENT'}
                                                    hidden={item.charge_name === 'ADJUSTMENT'}
                                                />
                                                )}
                                            </td>
                                            )}
                                            {!isTransporter && (
                                            <td>
                                                {receiptUploadOnly ? (
                                                    <div style={{ padding: '8px' }}>{unitOptions.find(u => u.id === item.unit_type)?.text || item.unit_type}</div>
                                                ) : (
                                                <Select2Dropdown
                                                    options={unitOptions}
                                                    value={item.unit_type}
                                                    onChange={val => updateItem(idx, 'unit_type', val)}
                                                    valueKey="id"
                                                    displayKey="text"
                                                    isDisabled={isFieldDisabled || item.charge_name === 'ADJUSTMENT'}
                                                    placeholder="TYPE"
                                                    hidden={item.charge_name === 'ADJUSTMENT'}
                                                />
                                                )}
                                            </td>
                                            )}
                                            {!isTransporter && (
                                            <td>
                                                {receiptUploadOnly ? (
                                                    <div style={{ padding: '8px', textAlign: 'right' }}>{formatCurrency(item.unit_rate)}</div>
                                                ) : (
                                                <FormattedNumberInput
                                                    value={item.unit_rate}
                                                    onChange={val => updateItem(idx, 'unit_rate', val)}
                                                    disabled={isFieldDisabled || item.charge_name === 'ADJUSTMENT'}
                                                    hidden={item.charge_name === 'ADJUSTMENT'}
                                                />
                                                )}
                                            </td>
                                            )}
                                            {invoiceCurrencyDifferent && (
                                                <td>
                                                    {receiptUploadOnly ? (
                                                        <div style={{ padding: '8px', textAlign: 'right' }}>{formatCurrency(item.foreign_amount || (parseFloat(item.units || 0) * parseFloat(item.unit_rate || 0)))}</div>
                                                    ) : (
                                                        <FormattedNumberInput
                                                            value={item.foreign_amount || (parseFloat(item.units || 0) * parseFloat(item.unit_rate || 0))}
                                                            onChange={val => updateItem(idx, 'foreign_amount', val)}
                                                            disabled={isFieldDisabled}
                                                            style={{ textAlign: 'right' }}
                                                            hidden={item.charge_name === 'ADJUSTMENT'}
                                                        />
                                                    )}
                                                </td>
                                            )}
                                            {invoiceCurrencyDifferent && (
                                                <td>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={item.add_vat ? formatCurrency(itemForeignVat(item)) : '0.00'}
                                                        readOnly
                                                        style={{ textAlign: 'right', background: '#f8f9fa' }}
                                                        hidden={item.charge_name === 'ADJUSTMENT'}
                                                    />
                                                </td>
                                            )}
                                            {invoiceCurrencyDifferent && (
                                                <td>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={item.charge_name !== 'ADJUSTMENT' ? formatCurrency(itemForeignTotal(item)) : '0.00'}
                                                        readOnly
                                                        style={{ textAlign: 'right', background: '#f8f9fa', fontWeight: 'bold' }}
                                                        hidden={item.charge_name === 'ADJUSTMENT'}
                                                    />
                                                </td>
                                            )}

                                            <td>
                                                {receiptUploadOnly ? (
                                                    <div style={{ textAlign: 'right', fontWeight: 'bold', padding: '8px' }}>{formatCurrency((invoiceCurrencyDifferent && !item.foreign_amount) ? item.amount * (fxRoundOff ? Math.round(parseFloat(invoiceCurrencyData.fx_rate || 1)) : parseFloat(invoiceCurrencyData.fx_rate || 1)) : item.amount)}</div>
                                                ) : (
                                                <div>
                                                    <FormattedNumberInput
                                                        value={
                                                            item.charge_name === 'ADJUSTMENT' ? item.total_amount || item.amount : (isView && invoiceCurrencyDifferent && !item.foreign_amount) ? item.amount * (fxRoundOff ? Math.round(parseFloat(invoiceCurrencyData.fx_rate || 1)) : parseFloat(invoiceCurrencyData.fx_rate || 1)) : item.amount
                                                        }
                                                        onChange={val => updateItem(idx, 'amount', val)}
                                                        disabled={isFieldDisabled || item.charge_name !== 'ADJUSTMENT'}
                                                        style={{ textAlign: 'right', fontWeight: 'bold' }}
                                                        allowNegative={item.charge_name === 'ADJUSTMENT'}
                                                    />
                                                    {adjustmentErrors[idx] && (
                                                        <div style={{ color: 'red', fontSize: '11px', marginTop: '2px' }}>{adjustmentErrors[idx]}</div>
                                                    )}
                                                </div>
                                                )}
                                            </td>
                                            <td className="text-center" style={{ verticalAlign: 'middle' }}>
                                                {receiptUploadOnly ? (
                                                    <span style={{fontWeight: 'bold'}}>{item.add_vat ? formatCurrency(item.vat_amount || (item.amount * 0.18)) : '0.00'}</span>
                                                ) : (
                                                <input
                                                    type="checkbox"
                                                    checked={item.add_vat}
                                                    onChange={() => toggleItemVat(idx)}
                                                    disabled={isFieldDisabled || item.charge_name === 'ADJUSTMENT'}
                                                    style={{ transform: 'scale(1.3)' }}
                                                    hidden={item.charge_name === 'ADJUSTMENT'}
                                                />
                                                )}
                                            </td>
                                            <td>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={item.add_vat ? formatCurrency(item.vat_amount || (item.amount * 0.18)) : '0.00'}
                                                    readOnly
                                                    style={{ textAlign: 'right', background: '#f8f9fa' }}
                                                />
                                            </td>
                                            <td>
                                                {receiptUploadOnly ? (
                                                    <div style={{ textAlign: 'right', fontWeight: 'bold', padding: '8px' }}>{item.total_amount ? formatCurrency(item.total_amount) : formatCurrency(item.add_vat ? (item.amount * 1.18) : item.amount)}</div>
                                                ) : isTransporter ? (
                                                    <FormattedNumberInput
                                                        value={item.total_amount || ''}
                                                        onChange={val => updateItem(idx, 'total_amount', val)}
                                                        disabled={isFieldDisabled}
                                                        style={{ textAlign: 'right', fontWeight: 'bold' }}
                                                    />
                                                ) : (
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={item.total_amount ? formatCurrency(item.total_amount) : formatCurrency(item.add_vat ? (item.amount * 1.18) : item.amount)}
                                                        readOnly
                                                        style={{ textAlign: 'right', background: '#f8f9fa', fontWeight: 'bold' }}
                                                    />
                                                )}
                                            </td>
                                            
                                            {!isFieldDisabled && <td><button type="button" className="btn btn-sm btn-danger" onClick={() => removeItem(idx)}>&times;</button></td>}
                                        </>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr style={{ background: '#f8f9fa' }}>
                                <td colSpan={isTransporter ? 1 : 4} style={{ textAlign: 'right', fontWeight: 'bold' }}>TOTAL</td>
                                {invoiceCurrencyDifferent && (
                                    <td style={{ textAlign: 'right', fontWeight: 'bold' }}>
                                        {formatCurrency(items.reduce((s, i) => s + (i.charge_name === 'ADJUSTMENT' ? 0 : itemForeignAmount(i)), 0))}
                                    </td>
                                )}
                                {invoiceCurrencyDifferent && (
                                    <td style={{ textAlign: 'right', fontWeight: 'bold' }}>
                                        {formatCurrency(items.reduce((s, i) => s + itemForeignVat(i), 0))}
                                    </td>
                                )}
                                {invoiceCurrencyDifferent && (
                                    <td style={{ textAlign: 'right', fontWeight: 'bold' }}>
                                        {formatCurrency(totalForeignAmount)}
                                    </td>
                                )}
                                <td style={{ textAlign: 'right', fontWeight: 'bold' }}>
                                    {formatCurrency(netTotal)}
                                </td>
                                <td></td>
                                <td style={{ textAlign: 'right', fontWeight: 'bold' }}>
                                    {formatCurrency(vatTotal)}
                                </td>
                                <td className="flash-amount" style={{ textAlign: 'right', fontWeight: 'bold' }}>
                                    {formatCurrency(grossTotal)}
                                </td>
                                {!isFieldDisabled && <td></td>}
                            </tr>
                            {!isFieldDisabled && (
                                <tr>
                                    <td colSpan={isTransporter ? (invoiceCurrencyDifferent ? 9 : 6) : (invoiceCurrencyDifferent ? 12 : 9)}>
                                        <button type="button" className="btn btn-sm btn-primary" onClick={addItem}>+ ADD LINE</button>
                                    </td>
                                </tr>
                            )}
                        </tfoot>
                        </table>

                        {/* TOTALS */}
                        <div style={{ marginTop: '15px', display: 'flex', gap: '2px' }}>
                            <div style={{ flex: 1, background: '#f8f9fa', padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>
                                <div style={{ fontSize: '11px', color: '#666' }}>NET TOTAL</div>
                                <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{formData.currency} {formatCurrency(netTotal)}</div>
                                {formData.currency !== 'TZS' && (
                                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px', borderTop: '1px dashed #ccc', paddingTop: '4px' }}>
                                        TZS {formatCurrency(netTotal * parseFloat(formData.usd_rate || 1))}
                                    </div>
                                )}
                            </div>
                            <div style={{ flex: 1, background: '#f8f9fa', padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>
                                <div style={{ fontSize: '11px', color: '#666' }}>VAT (18%)</div>
                                <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{formData.currency} {formatCurrency(vatTotal)}</div>
                                {formData.currency !== 'TZS' && (
                                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px', borderTop: '1px dashed #ccc', paddingTop: '4px' }}>
                                        TZS {formatCurrency(vatTotal * parseFloat(formData.usd_rate || 1))}
                                    </div>
                                )}
                            </div>
                            <div style={{ flex: 1, background: '#e8f0fe', padding: '10px', border: '1px solid #b8daff', textAlign: 'center' }}>
                                <div style={{ fontSize: '11px', color: '#004085' }}>TOTAL PAYABLE</div>
                                <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#004085' }}>{formData.currency} {formatCurrency(grossTotal)}</div>
                                {formData.currency !== 'TZS' && (
                                    <div style={{ fontSize: '12px', color: '#004085', marginTop: '4px', borderTop: '1px dashed #ccc', paddingTop: '4px' }}>
                                        TZS {formatCurrency(grossTotal * parseFloat(formData.usd_rate || 1))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* DOCUMENTS */}
                    <div className="form-section" style={{ marginTop: '20px' }}>
                        <h4>DOCUMENTS</h4>
                        <table className="table table-sm" style={{ fontSize: '12px', marginBottom: 0, overflow: 'visible' }}>
                            <thead>
                                <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                                    <th style={{ textAlign: 'left', border: 'none', paddingBottom: '4px', width: '1%', whiteSpace: 'nowrap' }}>DOCUMENT TYPE</th>
                                    <th style={{ textAlign: 'left', border: 'none', paddingBottom: '4px' }}>VIEW</th>
                                    {!isInvoiceEditDisabled && <th style={{ width: '30px', border: 'none' }}></th>}
                                </tr>
                            </thead>
                            <tbody style={{ overflow: 'visible' }}>
                                {documents.map((doc, idx) => {
                                    const isExistingDocInReceiptMode = receiptUploadOnly && (doc.existingUrl || doc.id);
                                    const normalizedDocType = (doc.type || '').toLowerCase();
                                    const isReplaceEligibleType = normalizedDocType === 'tax invoice' || normalizedDocType === 'receipt';
                                    const isAssessmentNoticeDoc = normalizedDocType.includes('assessment');
                                    return (
                                    <tr key={idx} style={{ borderBottom: '1px solid #f0f0f0', overflow: 'visible' }}>
                                        <td style={{ padding: '3px 6px', verticalAlign: 'middle', border: 'none', width: '1%', whiteSpace: 'nowrap', overflow: 'visible', position: 'relative' }}>
                                            {isExistingDocInReceiptMode || ['bl', 'bill of lading'].includes((doc.type || '').toLowerCase()) || doc.isJobDocument || isAssessmentNoticeDoc ? (
                                                <strong style={{ fontSize: '12px' }}>
                                                    {doc.type === 'Other' ? (doc.customName || 'Other') : doc.type}
                                                </strong>
                                            ) : (
                                                <div style={{ minWidth: 180, position: 'relative', overflow: 'visible' }}>
                                                    <Select2Dropdown
                                                        options={documentTypeOptions}
                                                        value={doc.type}
                                                        onChange={val => updateDocument(idx, 'type', val)}
                                                        valueKey="id"
                                                        displayKey="text"
                                                        placeholder="SELECT TYPE"
                                                        isDisabled={isInvoiceEditDisabled}
                                                        menuPlacement="top"
                                                        popoutMode={popoutMode}
                                                    />
                                                </div>
                                            )}
                                        </td>
                                        <td style={{ padding: '3px 6px', verticalAlign: 'middle', border: 'none' }}>
                                            {doc.existingUrl ? (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    <button
                                                        type="button"
                                                        onClick={(e) => { e.preventDefault(); openFilePreview(doc.existingUrl, doc.type === 'Other' ? (doc.customName || 'Other') : doc.type); }}
                                                        style={{ fontSize: 12, background: '#007bff', color: '#fff', border: 'none', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                                                        title="View Document"
                                                    >
                                                        View
                                                    </button>
                                                    {(!isInvoiceEditDisabled && !isExistingDocInReceiptMode && (!doc.isJobDocument || isAssessmentNoticeDoc) && !['bl', 'bill of lading'].includes((doc.type || '').toLowerCase())) && (
                                                        <input 
                                                            type="file" 
                                                            className="form-control-file" 
                                                            onChange={e => {
                                                                const f = e.target.files[0] || null;
                                                                setDocuments(prev => prev.map((d, i) =>
                                                                    i === idx ? { ...d, file: f, allowReplace: f ? true : d.allowReplace } : d
                                                                ));
                                                            }}
                                                            style={{ fontSize: '12px', maxWidth: '160px' }}
                                                        />
                                                    )}
                                                    {receiptUploadOnly && isExistingDocInReceiptMode && isReplaceEligibleType && doc.allowReplace && !isInvoiceEditDisabled && (
                                                        <>
                                                            <label htmlFor={`replace-doc-${idx}`} className="btn btn-sm btn-warning" style={{ marginBottom: 0, fontSize: '12px' }}>
                                                                REPLACE
                                                            </label>
                                                            <input
                                                                id={`replace-doc-${idx}`}
                                                                type="file"
                                                                style={{ display: 'none' }}
                                                                onChange={(e) => {
                                                                    const selected = e.target.files[0] || null;
                                                                    updateDocument(idx, 'file', selected);
                                                                    if (selected) updateDocument(idx, 'efdVerified', false);
                                                                }}
                                                            />
                                                        </>
                                                    )}
                                                    {isUploadedReceiptReview && isReplaceEligibleType && (
                                                        <div className="form-check" style={{ marginBottom: 0 }}>
                                                            <input
                                                                className="form-check-input"
                                                                type="checkbox"
                                                                id={`allow-replace-${idx}`}
                                                                checked={!!doc.allowReplace}
                                                                onChange={(e) => updateDocument(idx, 'allowReplace', e.target.checked)}
                                                            />
                                                            <label className="form-check-label" htmlFor={`allow-replace-${idx}`} style={{ fontWeight: 'bold', color: '#c0392b', fontSize: '11px', whiteSpace: 'nowrap' }}>
                                                                ALLOW REPLACE
                                                            </label>
                                                        </div>
                                                    )}
                                                    {receiptUploadOnly && isExistingDocInReceiptMode && isReplaceEligibleType && doc.allowReplace && doc.file && (
                                                        <>
                                                            <span style={{ fontSize: '11px', color: '#444' }}>{doc.file.name}</span>
                                                            <button
                                                                type="button"
                                                                onClick={() => { const url = URL.createObjectURL(doc.file); openFilePreview(url, doc.file.name); }}
                                                                style={{ fontSize: 12, background: '#007bff', color: '#fff', border: 'none', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                                                            >
                                                                View New
                                                            </button>
                                                            {((!isTransporter || (isTransporter && isStage1Approval)) && formData.control_no_type !== 'GEPG') && (
                                                                <div className="form-check" style={{ marginBottom: 0 }}>
                                                                    <input
                                                                        className="form-check-input"
                                                                        type="checkbox"
                                                                        id={`efd-replace-${idx}`}
                                                                        checked={doc.efdVerified || false}
                                                                        onChange={(e) => {
                                                                            if (e.target.checked) setShowEFDWarning(true);
                                                                            updateDocument(idx, 'efdVerified', e.target.checked);
                                                                        }}
                                                                    />
                                                                    <label className="form-check-label" htmlFor={`efd-replace-${idx}`} style={{ color: 'red', fontWeight: 'bold', fontSize: '11px', whiteSpace: 'nowrap' }}>
                                                                        EFD VERIFIED
                                                                    </label>
                                                                </div>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            ) : (
                                                (!isInvoiceEditDisabled && !['bl', 'bill of lading'].includes((doc.type || '').toLowerCase()) && (!doc.isJobDocument || isAssessmentNoticeDoc)) ? (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                        <input 
                                                            type="file" 
                                                            className="form-control-file" 
                                                            onChange={e => updateDocument(idx, 'file', e.target.files[0])} 
                                                            style={{ fontSize: '12px', maxWidth: '160px' }}
                                                        />
                                                        {doc.file && (
                                                            <button 
                                                                type="button"
                                                                onClick={() => { const url = URL.createObjectURL(doc.file); openFilePreview(url, doc.file.name); }}
                                                                style={{ fontSize: 12, background: '#007bff', color: '#fff', border: 'none', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                                                            >
                                                                View
                                                            </button>
                                                        )}
                                                        {((!isTransporter || (isTransporter && isStage1Approval)) && ['tax invoice', 'receipt'].includes(normalizedDocType) && formData.control_no_type !== 'GEPG') && (
                                                            <div className="form-check" style={{ marginBottom: 0 }}>
                                                                <input 
                                                                    className="form-check-input" 
                                                                    type="checkbox" 
                                                                    id={`efd-${idx}`}
                                                                    checked={doc.efdVerified || false}
                                                                    onChange={(e) => {
                                                                        if (e.target.checked) setShowEFDWarning(true);
                                                                        updateDocument(idx, 'efdVerified', e.target.checked);
                                                                    }}
                                                                />
                                                                <label className="form-check-label" htmlFor={`efd-${idx}`} style={{ color: 'red', fontWeight: 'bold', fontSize: '11px', whiteSpace: 'nowrap' }}>
                                                                    EFD VERIFIED
                                                                </label>
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : <span className="text-muted" style={{ fontSize: '12px' }}>No File</span>
                                            )}
                                        </td>
                                        {!isInvoiceEditDisabled && (
                                            <td style={{ padding: '3px 4px', verticalAlign: 'middle', border: 'none', textAlign: 'center' }}>
                                                {canManageDocumentRows && !isExistingDocInReceiptMode && (!doc.isJobDocument || isAssessmentNoticeDoc) && !['bl', 'bill of lading'].includes((doc.type || '').toLowerCase()) && (
                                                    <button type="button" className="btn btn-sm btn-danger" onClick={() => removeDocument(idx)}>&times;</button>
                                                )}
                                            </td>
                                        )}
                                    </tr>
                                    );
                                })}
                            </tbody>
                            {canManageDocumentRows && !isInvoiceEditDisabled && (
                                <tfoot>
                                    <tr>
                                        <td colSpan="3" style={{ border: 'none', paddingTop: '4px' }}><button type="button" className="btn btn-sm btn-secondary" onClick={addDocument}>ADD DOCUMENT</button></td>
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    </div>
                    </>
                )}
                </form>
            </div>

            <div className="dialog-footer">
                {(isStage2Approval || isStage3Approval) ? (
                     showRejectInput ? (
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', width: '100%' }}>
                            <div style={{ flex: 1 }}>
                                <small style={{ fontWeight: 'bold' }}>REASON:</small>
                                <input 
                                    type="text" 
                                    className="form-control" 
                                    placeholder="Enter Rejection Reason..." 
                                    value={rejectReason}
                                    onChange={(e) => setRejectReason(e.target.value)}
                                />
                            </div>
                            <div style={{ minWidth: '220px' }}>
                                 <small style={{ fontWeight: 'bold', fontSize: '9px' }}>SEND TO:</small>
                                 <Select2Dropdown
                                    options={getRejectionOptions()}
                                    value={rejectionTarget}
                                    onChange={setRejectionTarget}
                                    valueKey="id"
                                    displayKey="text"
                                    placeholder="SELECT TARGET"
                                    menuPlacement="top"
                                    popoutMode={popoutMode}
                                 />
                             </div>
                            <div style={{ paddingBottom: '150px' }}>
                                <button className="btn btn-danger" onClick={handleRejectionSubmit} disabled={actionLoading} style={{marginRight: '5px'}}>Confirm Reject</button>
                                <button className="btn btn-secondary" onClick={() => setShowRejectInput(false)}>Cancel</button>
                            </div>
                        </div>
                     ) : (
                        <>
                            {isStage3Approval && (
                                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '6px', marginRight: 'auto', flexWrap: 'nowrap' }}>
                                    <div style={{ minWidth: '200px' }}>
                                        {!isGepgPayable && !isCash && (
                                            <small style={{ fontWeight: 'bold', fontSize: '9px' }}>TISS / TIPS</small>
                                        )}
                                        {isGepgPayable || isCash ? (
                                            <div style={{ fontWeight: 'bold', lineHeight: '38px', fontSize: '12px' }}>NO TISS/TIPS</div>
                                        ) : (
                                            <div style={{ border: showStage3TissError ? '1px solid #dc3545' : '1px solid transparent', borderRadius: '4px', padding: showStage3TissError ? '2px' : 0 }}>
                                                <Select2Dropdown
                                                    options={[{ id: 'NO TISS/TIPS', text: 'NO TISS/TIPS' }, { id: 'TISS', text: 'TISS' }, { id: 'TIPS', text: 'TIPS' }]}
                                                    value={stage3Data.tiss_tips}
                                                    onChange={val => {
                                                        setStage3Data({ ...stage3Data, tiss_tips: val });
                                                        if (val) setShowStage3TissError(false);
                                                    }}
                                                    valueKey="id"
                                                    displayKey="text"
                                                    placeholder="PLEASE SELECT AN OPTION"
                                                    menuPlacement="top"
                                                    popoutMode={popoutMode}
                                                />
                                            </div>
                                        )}
                                        {showStage3TissError && !isGepgPayable && !isCash && (
                                            <small style={{ color: '#dc3545', display: 'block', marginTop: '4px' }}>TISS / TIPS selection is required.</small>
                                        )}
                                    </div>
                                    {isTissOrTipsSelected && (
                                    <div style={{ minWidth: '150px' }}>
                                        <small style={{ fontWeight: 'bold', fontSize: '9px' }}>TISS / TIPS AMOUNT (TZS)</small>
                                        <div style={{ fontWeight: 'bold', color: '#c0392b', lineHeight: '38px', fontSize: '12px' }}>
                                            {formatCurrency(stage3TissTipsAmount)}
                                        </div>
                                    </div>
                                    )}
                                    {isGepgPayable && gepgAmount > 0 && (
                                    <div style={{ minWidth: '150px' }}>
                                        <small style={{ fontWeight: 'bold', fontSize: '9px' }}>GEPG CHARGE (TZS)</small>
                                        <div style={{ fontWeight: 'bold', color: '#c0392b', lineHeight: '38px', fontSize: '12px' }}>
                                            {formatCurrency(gepgAmount)}
                                        </div>
                                    </div>
                                    )}
                                    {(isTissOrTipsSelected || (isGepgPayable && gepgAmount > 0)) && (
                                    <div style={{ minWidth: '165px' }}>
                                        <small style={{ fontWeight: 'bold', fontSize: '9px' }}>NEW PAYABLE (TZS)</small>
                                        <div style={{ fontWeight: 'bold', color: '#0b5ed7', lineHeight: '38px', fontSize: '12px' }}>
                                            {formatCurrency(stage3NewPayableTotal)}
                                        </div>
                                    </div>
                                    )}
                                </div>
                            )}
                            {isStage3Approval && (
                                <div style={{ minWidth: '160px' }}>
                                    <small style={{ fontWeight: 'bold', fontSize: '9px' }}>PAYMENT DATE</small>
                                    <input
                                        type="date"
                                        max={new Date().toISOString().split('T')[0]}
                                        className="form-control"
                                        style={{ fontSize: '1.1em' }}
                                        value={stage3Data.payment_date}
                                        onChange={e => setStage3Data({ ...stage3Data, payment_date: e.target.value })}
                                    />
                                </div>
                            )}
                            <button className="btn btn-success" 
                                onClick={isStage2Approval ? handleStage2Approve : handleStage3Approve} 
                                disabled={actionLoading} 
                                style={{ backgroundColor: 'green', borderColor: 'green', color: 'white' }}
                            >
                                Approve
                            </button>
                            <button className="btn btn-danger" onClick={() => { setRejectReason(''); setRejectionTarget(null); setShowRejectInput(true); }} disabled={actionLoading}>Reject</button>
                            <button className="dialog-btn btn-secondary" onClick={onClose}>Close</button>
                        </>
                     )
                ) : (isExpensesView && !receiptUploadOnly && !isUploadedReceiptReview) ? (
                     <button className="dialog-btn btn-secondary" onClick={onClose}>Close</button>
                ) : (
                    // Normal footer options
                    approvalMode ? (
                        showRejectInput ? (
                           <div style={{ display: 'flex', gap: '10px', alignItems: 'center', width: '100%' }}>
                               <input 
                                   type="text" 
                                   className="form-control" 
                                   placeholder="Enter Rejection Reason..." 
                                   value={rejectReason}
                                   onChange={(e) => setRejectReason(e.target.value)}
                                   style={{ flex: 1 }}
                               />
                               {/* Stage 1 Rejection goes to Creator automatically (REJECTED) */}
                               <button
                                   className="btn btn-danger"
                                   onClick={() => onReject(rejectReason, 'REJECTED', {
                                       allow_replace_tax_invoice: documents.some((d) => (d.type || '').toLowerCase() === 'tax invoice' && d.allowReplace),
                                       allow_replace_receipt: documents.some((d) => (d.type || '').toLowerCase() === 'receipt' && d.allowReplace),
                                   })}
                                   disabled={actionLoading}
                               >
                                   Confirm Reject
                               </button>
                               <button className="btn btn-secondary" onClick={() => setShowRejectInput(false)}>Cancel</button>
                           </div>
                        ) : (
                           <>
                               {!showClientPaidConfirm && canApprove && (!isUploadedReceiptReview || !hasAllowReplaceSelected) && (
                                   <button className="btn btn-success" onClick={handleApprove} disabled={actionLoading} style={{ backgroundColor: 'green', borderColor: 'green', color: 'white' }}>Approve</button>
                               )}
                               {!showClientPaidConfirm && (
                                   <button className="btn btn-danger" onClick={() => { setRejectReason(''); setShowRejectInput(true); }} disabled={actionLoading}>Reject</button>
                               )}
                               {canApprove && !hideClientPaid && (
                                   showClientPaidConfirm ? (
                                       <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                           <label style={{ fontWeight: 'bold', margin: 0, whiteSpace: 'nowrap', color: '#004085' }}>PAYMENT DATE:</label>
                                           <input
                                               type="date"
                                               max={new Date().toISOString().split('T')[0]}
                                               className="form-control"
                                               style={{ width: 'auto', fontSize: '1.1em' }}
                                               value={clientPaidDate}
                                               onChange={e => setClientPaidDate(e.target.value)}
                                           />
                                           <button
                                               className="btn btn-primary"
                                               onClick={handleConfirmClientPaid}
                                               disabled={actionLoading || !clientPaidDate}
                                               style={{ background: 'blue', borderColor: 'blue', whiteSpace: 'nowrap' }}
                                           >Confirm Client Paid</button>
                                           <button
                                               className="btn btn-secondary"
                                               onClick={() => setShowClientPaidConfirm(false)}
                                               disabled={actionLoading}
                                           >Cancel</button>
                                       </div>
                                   ) : (
                                       <button className="btn btn-primary" onClick={handleClientPaidClick} disabled={actionLoading} style={{ background: 'blue', borderColor: 'blue' }}>Client Paid</button>
                                   )
                               )}
                               <button className="dialog-btn btn-secondary" onClick={onClose}>Close</button>
                           </>
                        )
                    ) : (
                       <>
                           {!isView && <button className="dialog-btn btn-primary" onClick={handleSubmit} disabled={submitting || !formData.job}>SAVE PAYABLE</button>}
                           <button className="dialog-btn btn-secondary" onClick={onClose}>CLOSE</button>
                       </>
                    )
                )}
            </div>

            {/* EFD Warning Modal */}
            {showEFDWarning && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
                    backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                    <div style={{
                        backgroundColor: 'white', padding: '20px', borderRadius: '5px', width: '400px',
                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                    }}>
                        <h4 style={{ color: '#d32f2f', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>
                            <i className="fa fa-exclamation-triangle"></i> EFD VERIFICATION
                        </h4>
                        <div style={{ padding: '20px 0', fontSize: '1.1em', color: '#d32f2f', fontWeight: 'bold' }}>
                            Please ensure you have verified EFD or keep 1,000 TZS ready!
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <button className="btn btn-danger" onClick={() => setShowEFDWarning(false)}>OK</button>
                        </div>
                    </div>
                </div>
            )}

            {showSaveProceedDialog && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
                    backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                    <div style={{
                        backgroundColor: 'white', borderRadius: '6px', width: '520px', maxWidth: '95%', overflow: 'hidden',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                    }}>
                        <div style={{ background: '#0d6efd', color: '#fff', padding: '16px 20px' }}>
                            <h3 style={{ margin: 0, color: '#fff' }}>Save And Proceed</h3>
                        </div>
                        <div style={{ padding: '20px' }}>
                            <p style={{ margin: 0, fontSize: '1rem' }}>
                                Document needs to be saved first. Save and proceed?
                            </p>
                        </div>
                        <div className="dialog-footer">
                            <button
                                className="dialog-btn dialog-btn-primary"
                                onClick={async () => {
                                    setShowSaveProceedDialog(false);
                                    await handleSubmit({
                                        preventDefault: () => {},
                                        isClientPaidAction: true
                                    });
                                }}
                            >
                                Yes
                            </button>
                            <button
                                className="dialog-btn dialog-btn-secondary"
                                onClick={() => setShowSaveProceedDialog(false)}
                            >
                                No
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <ConfirmDialog
                open={confirmDialog.open}
                title={confirmDialog.title}
                message={confirmDialog.message}
                onConfirm={() => {
                    if (confirmDialog.onConfirm) confirmDialog.onConfirm();
                    closeConfirm();
                }}
                onCancel={closeConfirm}
            />

            <style>{`
                @keyframes flashRedWarning {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.2; }
                }
                .flash-red-warning {
                    color: #d32f2f;
                    font-weight: bold;
                    animation: flashRedWarning 1s infinite;
                }
            `}</style>

            {showExpensesModal && (
                <PopoutWindow
                    title={`Expenses - IDF #${initialData?.idf_number || initialData?.job_details?.idf_no || jobDetails?.idf_no}`}
                    name={`payable_expenses_${((initialData?.idf_number || initialData?.job_details?.idf_no || jobDetails?.idf_no) || '').replace(/\s+/g, '_')}`}
                    onClose={() => setShowExpensesModal(false)}
                >
                    <PayableExpensesView
                        idfNo={initialData?.idf_number || initialData?.job_details?.idf_no || jobDetails?.idf_no}
                        onClose={() => setShowExpensesModal(false)}
                    />
                </PopoutWindow>
            )}
        </div>
    );
};

export default OtherPayablesForm;

